import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { apiGet, apiPost, apiPut, apiDelete } from "../utils/api";
import { downloadSampleCsv, normalizePhoneFromImport } from "../utils/csvSamples";
import { useModal } from "../context/ModalContext";
import SendWindowPicker, { isSendWindowComplete } from "../components/SendWindowPicker";
import SpintaxHelp from "../components/flow/SpintaxHelp";
import {
  Card,
  CardBody,
  CardHeader,
  PageHeader,
  Spinner,
  Badge,
  EmptyState,
  TableWrapper,
  Table,
  Th,
  Td,
} from "../components/ui";
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaToggleOn,
  FaToggleOff,
  FaArrowLeft,
  FaTimes,
  FaSearch,
  FaEnvelope,
  FaClock,
  FaUsers,
  FaWhatsapp,
  FaCalendarAlt,
  FaCode,
  FaCheck,
  FaRocket,
  FaPaperPlane,
  FaReply,
  FaFilter,
  FaImage,
  FaBullhorn,
  FaChevronRight,
  FaUpload,
  FaSave,
  FaChartLine,
  FaCopy,
  FaUserPlus,
  FaTag,
  FaBuilding,
} from "react-icons/fa";

/* ── Variable Helper (reused from Nurturing) ── */
const VARIABLES = [
  { key: "{{firstName}}", desc: "First name", example: "there" },
  { key: "{{lastName}}", desc: "Last name", example: "" },
  { key: "{{name}}", desc: "Full name", example: "Friend" },
  { key: "{{phone}}", desc: "Phone number", example: "" },
  { key: "{{email}}", desc: "Email", example: "" },
  { key: "{{companyName}}", desc: "Company name", example: "" },
  { key: "{{tags}}", desc: "Tags (comma-separated)", example: "" },
  { key: "{{stage}}", desc: "Lead stage", example: "" },
];

const BUILTIN_VAR_KEYS = new Set([
  ...VARIABLES.map((v) => v.key.replace(/[{}]/g, "").trim().toLowerCase()),
  "company", // alias of companyName — never surface as a separate "From your CSV" entry
]);

function sanitizeVarName(raw) {
  return String(raw || "").trim().replace(/[^A-Za-z0-9_.\- ]/g, "");
}

function VariableHelper({ onInsert, extraVars = [] }) {
  const [open, setOpen] = useState(false);
  const [customName, setCustomName] = useState("");

  const insertCustom = () => {
    const clean = sanitizeVarName(customName);
    if (!clean) return;
    onInsert(`{{custom.${clean}}}`);
    setCustomName("");
    setOpen(false);
  };

  return (
    <div className="relative">
      <button
        type="button"
        className="flex items-center gap-1 text-xs text-brand hover:text-brand-700 font-medium transition-colors"
        onClick={() => setOpen(!open)}
      >
        <FaCode size={9} />
        Insert Variable
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 z-20 mt-1 w-80 bg-surface-card border border-surface-border rounded-xl shadow-xl py-2 max-h-96 overflow-y-auto">
            <p className="px-3 pb-1.5 text-2xs font-semibold text-ink-faint uppercase tracking-wider">
              Built-in
            </p>
            {VARIABLES.map((v) => (
              <button
                key={v.key}
                type="button"
                className="w-full text-left px-3 py-2 hover:bg-brand/5 flex items-center justify-between transition-colors"
                onClick={() => {
                  onInsert(v.example ? `${v.key.slice(0, -2)} | ${v.example}}}` : v.key);
                  setOpen(false);
                }}
              >
                <code className="text-xs font-mono text-brand">{v.key}</code>
                <span className="text-2xs text-ink-muted">{v.desc}</span>
              </button>
            ))}

            {extraVars.length > 0 && (
              <>
                <p className="px-3 pt-2 pb-1.5 mt-1 border-t border-surface-border text-2xs font-semibold text-ink-faint uppercase tracking-wider">
                  From your CSV
                </p>
                {extraVars.map((name) => {
                  const key = `{{${name}}}`;
                  return (
                    <button
                      key={name}
                      type="button"
                      className="w-full text-left px-3 py-2 hover:bg-brand/5 flex items-center justify-between transition-colors"
                      onClick={() => {
                        onInsert(key);
                        setOpen(false);
                      }}
                    >
                      <code className="text-xs font-mono text-brand">{key}</code>
                      <span className="text-2xs text-ink-muted">CSV column</span>
                    </button>
                  );
                })}
              </>
            )}

            <div className="px-3 pt-2 pb-2 mt-1 border-t border-surface-border">
              <p className="text-2xs font-semibold text-ink-faint uppercase tracking-wider mb-1.5">
                Custom variable
              </p>
              <div className="flex items-center gap-1.5">
                <span className="text-xs font-mono text-ink-muted">{"{{custom."}</span>
                <input
                  type="text"
                  className="input flex-1 py-1 text-xs"
                  placeholder="variableName"
                  value={customName}
                  onChange={(e) => setCustomName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      insertCustom();
                    }
                  }}
                />
                <span className="text-xs font-mono text-ink-muted">{"}}"}</span>
                <button
                  type="button"
                  className="btn-secondary btn-sm py-1 px-2 text-2xs"
                  onClick={insertCustom}
                  disabled={!sanitizeVarName(customName)}
                >
                  Add
                </button>
              </div>
              <p className="text-2xs text-ink-faint leading-relaxed mt-1.5">
                Resolves from any CSV header you import (skipped or unmapped columns become custom fields automatically).
              </p>
            </div>

            <div className="border-t border-surface-border mt-1 pt-2 px-3 pb-1">
              <p className="text-2xs text-ink-faint leading-relaxed">
                Add fallbacks: <code className="text-brand">{"{{firstName | there}}"}</code>
                <br />If the value is empty, the fallback text is used instead.
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function collectExtraVars(leads) {
  const seen = new Map();
  for (const lead of leads || []) {
    for (const f of lead?.customFields || []) {
      if (!f?.key) continue;
      const low = String(f.key).toLowerCase();
      if (BUILTIN_VAR_KEYS.has(low)) continue;
      if (!seen.has(low)) seen.set(low, f.key);
    }
  }
  return Array.from(seen.values());
}

/* ── Step number badge ── */
function StepBadge({ number, color = "bg-brand" }) {
  return (
    <div className={`w-7 h-7 rounded-lg ${color} flex items-center justify-center flex-shrink-0`}>
      <span className="text-xs font-bold text-white">{number}</span>
    </div>
  );
}

/* ── Wizard step indicator ── */
function StepIndicator({ current, steps }) {
  return (
    <div className="flex items-center gap-2 mb-6">
      {steps.map((label, i) => {
        const stepNum = i + 1;
        const active = stepNum === current;
        const done = stepNum < current;
        return (
          <React.Fragment key={stepNum}>
            {i > 0 && (
              <div className={`flex-1 h-0.5 ${done ? "bg-brand" : "bg-surface-border"}`} />
            )}
            <div className="flex items-center gap-2">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${
                  active
                    ? "bg-brand text-white"
                    : done
                    ? "bg-green-500 text-white"
                    : "bg-surface-border text-ink-muted"
                }`}
              >
                {done ? <FaCheck size={10} /> : stepNum}
              </div>
              <span
                className={`text-xs font-medium hidden sm:inline ${
                  active ? "text-ink-primary" : "text-ink-muted"
                }`}
              >
                {label}
              </span>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
}

const DURATION_OPTIONS = [
  { value: "week", label: "1 Week" },
  { value: "month", label: "1 Month" },
  { value: "year", label: "1 Year" },
  { value: "indefinite", label: "Indefinite" },
];

const STAGE_OPTIONS = ["new", "contacted", "qualified", "converted", "lost"];

/* ── Stat box for detail view ── */
function StatBox({ label, value, sub, icon: Icon, color = "bg-brand" }) {
  return (
    <Card>
      <CardBody className="p-4">
        <div className="flex items-center gap-3">
          <div className={`w-9 h-9 rounded-xl ${color} flex items-center justify-center flex-shrink-0`}>
            <Icon size={14} className="text-white" />
          </div>
          <div>
            <p className="text-2xs text-ink-muted uppercase tracking-wider">{label}</p>
            <p className="text-lg font-bold text-ink-primary">{value}</p>
            {sub && <p className="text-2xs text-ink-muted">{sub}</p>}
          </div>
        </div>
      </CardBody>
    </Card>
  );
}

/* ════════════════════════════════════════════════
   Wizard Step 1 - Setup
   ════════════════════════════════════════════════ */
function WizardStep1({ form, setForm, sessions }) {
  const toggleSession = (sid) => {
    setForm((prev) => {
      const current = prev.sessionIds || [];
      return {
        ...prev,
        sessionIds: current.includes(sid)
          ? current.filter((id) => id !== sid)
          : [...current, sid],
      };
    });
  };

  return (
    <div className="space-y-5">
      <Card>
        <CardBody className="p-5">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-8 rounded-lg bg-green-500 flex items-center justify-center">
              <FaWhatsapp size={13} className="text-white" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-ink-primary">Campaign Setup</h3>
              <p className="text-2xs text-ink-muted mt-0.5">Name, sessions, and duration</p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="input-group">
              <label className="input-label">Campaign Name</label>
              <input
                className="input"
                placeholder="e.g. Welcome Drip, Re-engagement"
                value={form.name}
                onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              />
            </div>

            <div className="input-group">
              <label className="input-label">WhatsApp Sessions</label>
              {sessions.length === 0 ? (
                <p className="input-hint text-amber-600">No connected sessions. Connect one first.</p>
              ) : (
                <div className="space-y-2 max-h-48 overflow-y-auto border border-surface-border rounded-lg p-3">
                  {sessions.map((s) => {
                    const checked = (form.sessionIds || []).includes(s.sessionId);
                    return (
                      <label
                        key={s.sessionId}
                        className={`flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer transition-colors ${
                          checked ? "bg-brand/5 border border-brand/20" : "hover:bg-surface-bg border border-transparent"
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleSession(s.sessionId)}
                          className="accent-brand"
                        />
                        <div>
                          <p className="text-xs font-medium text-ink-primary">
                            {s.number || s.sessionId}
                            {s.transportType === 'walytic_browser' && (
                              <span className="ml-1.5 px-1 py-0.5 rounded text-2xs bg-brand/10 text-brand">browser</span>
                            )}
                            {s.transportType === 'walytic_cloud' && (
                              <span className="ml-1.5 px-1 py-0.5 rounded text-2xs bg-blue-50 text-blue-600">cloud</span>
                            )}
                          </p>
                          {s.name && (
                            <p className="text-2xs text-ink-muted">{s.name}</p>
                          )}
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}
              <p className="input-hint">
                {(form.sessionIds || []).length} session{(form.sessionIds || []).length !== 1 ? "s" : ""} selected
              </p>
            </div>

            <div className="input-group">
              <label className="input-label">Duration</label>
              <select
                className="input"
                value={form.duration}
                onChange={(e) => setForm((p) => ({ ...p, duration: e.target.value }))}
              >
                {DURATION_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              <p className="input-hint">How long the campaign runs per lead.</p>
            </div>

            <div className="input-group">
              <label className="input-label">Reply Behaviour</label>
              <label className="flex items-start gap-2 text-sm cursor-pointer p-2 rounded-lg border border-surface-border hover:bg-surface-bg transition-colors">
                <input
                  type="checkbox"
                  className="mt-0.5 accent-brand"
                  checked={form.stopOnReply !== false}
                  onChange={(e) => setForm((p) => ({ ...p, stopOnReply: e.target.checked }))}
                />
                <span>
                  <span className="font-medium text-ink-primary">Stop sending follow-ups when a contact replies</span>
                  <span className="text-2xs text-ink-faint block mt-0.5">
                    On (default) = engagement mode. Off = nurture mode (drip continues regardless of replies).
                  </span>
                </span>
              </label>
            </div>

            <div className="input-group">
              <label className="input-label">Pace between contacting each lead</label>
              <div className="space-y-2">
                <label
                  className={`flex items-start gap-2 text-sm cursor-pointer p-2.5 rounded-lg border transition-colors ${
                    form.pacingMode !== "custom"
                      ? "border-brand bg-brand/5"
                      : "border-surface-border hover:bg-surface-bg"
                  }`}
                >
                  <input
                    type="radio"
                    name="pacingMode"
                    className="mt-0.5 accent-brand"
                    checked={form.pacingMode !== "custom"}
                    onChange={() => setForm((p) => ({ ...p, pacingMode: "auto" }))}
                  />
                  <span>
                    <span className="font-medium text-ink-primary">Auto (recommended)</span>
                    <span className="text-2xs text-ink-faint block mt-0.5">
                      Random 10–90 seconds between each lead. Mimics human-paced sending and lowers ban risk.
                    </span>
                  </span>
                </label>
                <label
                  className={`flex items-start gap-2 text-sm cursor-pointer p-2.5 rounded-lg border transition-colors ${
                    form.pacingMode === "custom"
                      ? "border-brand bg-brand/5"
                      : "border-surface-border hover:bg-surface-bg"
                  }`}
                >
                  <input
                    type="radio"
                    name="pacingMode"
                    className="mt-0.5 accent-brand"
                    checked={form.pacingMode === "custom"}
                    onChange={() =>
                      setForm((p) => ({
                        ...p,
                        pacingMode: "custom",
                        pacingValue: p.pacingValue || 5,
                        pacingUnit: p.pacingUnit || "minutes",
                      }))
                    }
                  />
                  <span className="flex-1 min-w-0">
                    <span className="font-medium text-ink-primary">Custom interval</span>
                    {form.pacingMode === "custom" && (
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        <input
                          type="number"
                          className="input w-20 text-center"
                          min={1}
                          value={form.pacingValue ?? 5}
                          onChange={(e) =>
                            setForm((p) => ({
                              ...p,
                              pacingValue: Math.max(1, Number(e.target.value) || 1),
                            }))
                          }
                        />
                        <select
                          className="input w-32"
                          value={form.pacingUnit || "minutes"}
                          onChange={(e) => setForm((p) => ({ ...p, pacingUnit: e.target.value }))}
                        >
                          <option value="minutes">Minutes</option>
                          <option value="hours">Hours</option>
                          <option value="days">Days</option>
                        </select>
                        <span className="text-2xs text-ink-muted">between each lead</span>
                      </div>
                    )}
                    {form.pacingMode !== "custom" && (
                      <span className="text-2xs text-ink-faint block mt-0.5">
                        Set a fixed gap (e.g. 5 minutes) between each lead.
                      </span>
                    )}
                  </span>
                </label>
              </div>
            </div>
          </div>
        </CardBody>
      </Card>

      <SendWindowPicker
        value={form.sendWindow}
        onChange={(sw) => setForm((p) => ({ ...p, sendWindow: sw }))}
      />

      <Card>
        <CardBody>
          <div className="input-group">
            <label className="input-label">Daily send limit (per WhatsApp number)</label>
            <label className="flex items-start gap-2 text-sm cursor-pointer p-2 rounded-lg border border-surface-border hover:bg-surface-bg transition-colors">
              <input
                type="checkbox"
                className="mt-0.5 accent-brand"
                checked={!!form.dailyLimitEnabled}
                onChange={(e) => setForm((p) => ({ ...p, dailyLimitEnabled: e.target.checked }))}
              />
              <span className="flex-1 min-w-0">
                <span className="font-medium text-ink-primary">Cap how many messages each number sends per day</span>
                {form.dailyLimitEnabled ? (
                  <div className="mt-2 space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-2xs text-ink-muted w-24">Weekdays</span>
                      <input
                        type="number"
                        min={1}
                        className="input w-20 text-center"
                        value={form.dailyLimitMin ?? 80}
                        onChange={(e) => setForm((p) => ({ ...p, dailyLimitMin: Math.max(1, Number(e.target.value) || 1) }))}
                      />
                      <span className="text-2xs text-ink-muted">to</span>
                      <input
                        type="number"
                        min={1}
                        className="input w-20 text-center"
                        value={form.dailyLimitMax ?? 100}
                        onChange={(e) => setForm((p) => ({ ...p, dailyLimitMax: Math.max(1, Number(e.target.value) || 1) }))}
                      />
                      <span className="text-2xs text-ink-muted">msgs/day (randomized each day)</span>
                    </div>
                    <label className="flex items-center gap-2 text-2xs text-ink-muted cursor-pointer">
                      <input
                        type="checkbox"
                        className="accent-brand"
                        checked={!!form.weekendOverrideEnabled}
                        onChange={(e) => setForm((p) => ({ ...p, weekendOverrideEnabled: e.target.checked }))}
                      />
                      Use a different range on weekends (Sat/Sun)
                    </label>
                    {form.weekendOverrideEnabled && (
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-2xs text-ink-muted w-24">Weekends</span>
                        <input
                          type="number"
                          min={0}
                          className="input w-20 text-center"
                          value={form.weekendDailyLimitMin ?? 30}
                          onChange={(e) => setForm((p) => ({ ...p, weekendDailyLimitMin: Math.max(0, Number(e.target.value) || 0) }))}
                        />
                        <span className="text-2xs text-ink-muted">to</span>
                        <input
                          type="number"
                          min={0}
                          className="input w-20 text-center"
                          value={form.weekendDailyLimitMax ?? 50}
                          onChange={(e) => setForm((p) => ({ ...p, weekendDailyLimitMax: Math.max(0, Number(e.target.value) || 0) }))}
                        />
                        <span className="text-2xs text-ink-muted">msgs/day</span>
                      </div>
                    )}
                  </div>
                ) : (
                  <span className="text-2xs text-ink-faint block mt-0.5">
                    Off = unlimited per day (only the per-lead pace + send window apply). Turn on to hold each number to, e.g., 80–100/day.
                  </span>
                )}
              </span>
            </label>
          </div>

          <div className="input-group">
            <label className="input-label">Rest between batches</label>
            <label className="flex items-start gap-2 text-sm cursor-pointer p-2 rounded-lg border border-surface-border hover:bg-surface-bg transition-colors">
              <input
                type="checkbox"
                className="mt-0.5 accent-brand"
                checked={!!form.batchRestEnabled}
                onChange={(e) => setForm((p) => ({ ...p, batchRestEnabled: e.target.checked }))}
              />
              <span className="flex-1 min-w-0">
                <span className="font-medium text-ink-primary">Take a break after every batch of sends</span>
                {form.batchRestEnabled ? (
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <span className="text-2xs text-ink-muted">After every</span>
                    <input
                      type="number"
                      min={1}
                      className="input w-16 text-center"
                      value={form.restEveryMin ?? 10}
                      onChange={(e) => setForm((p) => ({ ...p, restEveryMin: Math.max(1, Number(e.target.value) || 1) }))}
                    />
                    <span className="text-2xs text-ink-muted">–</span>
                    <input
                      type="number"
                      min={1}
                      className="input w-16 text-center"
                      value={form.restEveryMax ?? 20}
                      onChange={(e) => setForm((p) => ({ ...p, restEveryMax: Math.max(1, Number(e.target.value) || 1) }))}
                    />
                    <span className="text-2xs text-ink-muted">msgs, rest</span>
                    <input
                      type="number"
                      min={1}
                      className="input w-16 text-center"
                      value={form.restDurationMinMinutes ?? 30}
                      onChange={(e) => setForm((p) => ({ ...p, restDurationMinMinutes: Math.max(1, Number(e.target.value) || 1) }))}
                    />
                    <span className="text-2xs text-ink-muted">–</span>
                    <input
                      type="number"
                      min={1}
                      className="input w-16 text-center"
                      value={form.restDurationMaxMinutes ?? 60}
                      onChange={(e) => setForm((p) => ({ ...p, restDurationMaxMinutes: Math.max(1, Number(e.target.value) || 1) }))}
                    />
                    <span className="text-2xs text-ink-muted">minutes</span>
                  </div>
                ) : (
                  <span className="text-2xs text-ink-faint block mt-0.5">
                    Off = no batch breaks. Turn on to pause, e.g., 30–60 min after every 10–20 messages — mimics a human rhythm.
                  </span>
                )}
              </span>
            </label>
          </div>
        </CardBody>
      </Card>
    </div>
  );
}

/* ════════════════════════════════════════════════
   Wizard Step 2 - Target Leads
   ════════════════════════════════════════════════ */
/* ── Simple CSV parser (handles quoted values) ── */
function parseCSVText(text) {
  const rows = [];
  let current = "";
  let inQuotes = false;
  const lines = text.split(/\r?\n/);
  for (const line of lines) {
    if (inQuotes) {
      current += "\n" + line;
      if (line.includes('"')) { inQuotes = false; }
    } else {
      current = line;
    }
    if (!inQuotes) {
      const row = [];
      let field = "";
      let q = false;
      for (let i = 0; i < current.length; i++) {
        const ch = current[i];
        if (q) {
          if (ch === '"' && current[i + 1] === '"') { field += '"'; i++; }
          else if (ch === '"') { q = false; }
          else { field += ch; }
        } else {
          if (ch === '"') { q = true; }
          else if (ch === "," || ch === "\t") { row.push(field.trim()); field = ""; }
          else { field += ch; }
        }
      }
      row.push(field.trim());
      if (row.some((c) => c)) rows.push(row);
      current = "";
    }
  }
  return rows;
}

const CSV_MAPPING_OPTIONS = [
  { value: "custom", label: "Custom variable" },
  { value: "skip", label: "Skip (ignore)" },
  { value: "phone", label: "Phone (required)" },
  { value: "firstName", label: "First Name" },
  { value: "lastName", label: "Last Name" },
  { value: "email", label: "Email" },
  { value: "companyName", label: "Company" },
  { value: "tags", label: "Tags" },
];

const CSV_TYPE_META = {
  custom:      { Icon: FaPlus,       bg: "bg-green-100",  color: "text-green-600" },
  skip:        { Icon: FaTimes,      bg: "bg-gray-100",   color: "text-gray-500" },
  phone:       { Icon: FaWhatsapp,   bg: "bg-emerald-100",color: "text-emerald-600" },
  firstName:   { Icon: FaUserPlus,   bg: "bg-orange-100", color: "text-orange-600" },
  lastName:    { Icon: FaUserPlus,   bg: "bg-orange-100", color: "text-orange-600" },
  email:       { Icon: FaEnvelope,   bg: "bg-blue-100",   color: "text-blue-600" },
  companyName: { Icon: FaBuilding,   bg: "bg-purple-100", color: "text-purple-600" },
  tags:        { Icon: FaTag,        bg: "bg-amber-100",  color: "text-amber-600" },
};

function WizardStep2({ form, setForm }) {
  const [targetMode, setTargetMode] = useState(form.targetType || "manual");
  const [leadSearch, setLeadSearch] = useState("");
  const [leadResults, setLeadResults] = useState([]);
  const [searchingLeads, setSearchingLeads] = useState(false);

  // Filter mode state
  const [tagInput, setTagInput] = useState("");
  const [filterPreview, setFilterPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);

  // CSV mode state
  const [csvRawRows, setCsvRawRows] = useState([]);
  const [csvMapping, setCsvMapping] = useState([]);
  const [csvFileName, setCsvFileName] = useState("");

  const searchLeads = useCallback(async (q) => {
    if (!q || q.length < 2) { setLeadResults([]); return; }
    setSearchingLeads(true);
    try {
      const res = await apiGet(`/api/contacts?isLead=true&search=${encodeURIComponent(q)}&limit=20`);
      setLeadResults(res?.contacts || res?.leads || res?.data || []);
    } catch { setLeadResults([]); }
    finally { setSearchingLeads(false); }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => searchLeads(leadSearch), 300);
    return () => clearTimeout(timer);
  }, [leadSearch, searchLeads]);

  const addLead = (lead) => {
    if ((form.leads || []).find((l) => l.id === lead.id)) return;
    setForm((p) => ({ ...p, leads: [...(p.leads || []), lead] }));
    setLeadSearch("");
    setLeadResults([]);
  };

  const removeLead = (leadId) => {
    setForm((p) => ({ ...p, leads: (p.leads || []).filter((l) => l.id !== leadId) }));
  };

  const addTag = (tag) => {
    const trimmed = tag.trim();
    if (!trimmed) return;
    const current = form.targetFilters?.tags || [];
    if (current.includes(trimmed)) return;
    setForm((p) => ({
      ...p,
      targetType: "filter",
      targetFilters: { ...(p.targetFilters || {}), tags: [...current, trimmed] },
    }));
    setTagInput("");
  };

  const removeTag = (tag) => {
    setForm((p) => ({
      ...p,
      targetFilters: {
        ...(p.targetFilters || {}),
        tags: ((p.targetFilters || {}).tags || []).filter((t) => t !== tag),
      },
    }));
  };

  const toggleStage = (stage) => {
    const current = form.targetFilters?.stages || [];
    const next = current.includes(stage)
      ? current.filter((s) => s !== stage)
      : [...current, stage];
    setForm((p) => ({
      ...p,
      targetType: "filter",
      targetFilters: { ...(p.targetFilters || {}), stages: next },
    }));
  };

  const previewFilters = async () => {
    setPreviewing(true);
    try {
      const res = await apiPost("/api/outreach/resolve-filters", {
        tags: form.targetFilters?.tags || [],
        stages: form.targetFilters?.stages || [],
      });
      setFilterPreview(res);
    } catch {
      setFilterPreview(null);
    } finally {
      setPreviewing(false);
    }
  };

  const handleCsvFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCsvFileName(file.name);
    let rows = [];
    const ext = file.name.split(".").pop().toLowerCase();
    if (ext === "xlsx" || ext === "xls") {
      try {
        const ExcelJS = (await import("exceljs")).default;
        const wb = new ExcelJS.Workbook();
        const buf = await file.arrayBuffer();
        await wb.xlsx.load(buf);
        const ws = wb.worksheets[0];
        ws.eachRow((row) => {
          rows.push(row.values.slice(1).map((v) => (v != null ? String(v) : "")));
        });
      } catch {
        rows = [];
      }
    } else {
      const text = await file.text();
      rows = parseCSVText(text);
    }
    if (rows.length === 0) return;
    setCsvRawRows(rows);
    // Auto-guess mapping from header row. Unknown headers default to
    // "custom" so they're available as {{HeaderName}} variables — users
    // can switch to "Skip (ignore)" if they don't want to surface them.
    const header = rows[0] || [];
    const guessMap = header.map((h) => {
      const low = (h || "").toLowerCase().replace(/[^a-z]/g, "");
      if (!low) return "skip";
      if (low.includes("phone") || low.includes("mobile") || low.includes("whatsapp")) return "phone";
      if (low.includes("first") && low.includes("name")) return "firstName";
      if (low.includes("last") && low.includes("name")) return "lastName";
      if (low === "name" || low === "fullname") return "firstName";
      if (low.includes("email") || low.includes("mail")) return "email";
      if (low.includes("company") || low.includes("org")) return "companyName";
      if (low.includes("tag")) return "tags";
      return "custom";
    });
    setCsvMapping(guessMap);
  };

  const applyCsvMapping = useCallback(() => {
    if (csvRawRows.length < 2) return;
    const phoneIdx = csvMapping.indexOf("phone");
    if (phoneIdx === -1) return;
    const headerRow = csvRawRows[0] || [];
    const dataRows = csvRawRows.slice(1); // skip header
    const leads = dataRows.map((row) => {
      const lead = {};
      const customFields = [];
      csvMapping.forEach((field, ci) => {
        const value = row[ci];
        if (!value) return;
        if (field === "skip") {
          // Truly ignored — column is not stored anywhere.
        } else if (field === "custom") {
          // Stored on contact.customFields with the original header as the
          // key, available in messages as {{HeaderName}} or {{custom.HeaderName}}.
          const key = String(headerRow[ci] || "").trim();
          if (key) customFields.push({ key, value: String(value).trim() });
        } else if (field === "phone") {
          // Recover scientific-notation digits (Excel mangles long numbers on save).
          lead.phone = normalizePhoneFromImport(value);
        } else {
          lead[field] = value;
        }
      });
      if (customFields.length > 0) lead.customFields = customFields;
      return lead;
    }).filter((l) => l.phone);
    setForm((p) => ({ ...p, csvLeads: leads, targetType: "csv" }));
  }, [csvRawRows, csvMapping, setForm]);

  useEffect(() => {
    if (targetMode === "csv" && csvRawRows.length > 1) applyCsvMapping();
  }, [csvMapping, csvRawRows, targetMode, applyCsvMapping]);

  useEffect(() => {
    setForm((p) => ({ ...p, targetType: targetMode }));
  }, [targetMode, setForm]);

  return (
    <Card>
      <CardBody className="p-5">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-8 rounded-lg bg-indigo-500 flex items-center justify-center">
            <FaUsers size={13} className="text-white" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-ink-primary">Target Leads</h3>
            <p className="text-2xs text-ink-muted mt-0.5">Choose who receives this campaign</p>
          </div>
        </div>

        {/* Mode toggle */}
        <div className="flex gap-2 mb-5">
          {["manual", "filter", "csv"].map((mode) => (
            <button
              key={mode}
              type="button"
              className={`px-4 py-2 rounded-lg text-xs font-medium transition-colors ${
                targetMode === mode
                  ? "bg-brand text-white"
                  : "bg-surface-bg text-ink-muted hover:text-ink-primary border border-surface-border"
              }`}
              onClick={() => setTargetMode(mode)}
            >
              {mode === "manual" ? "Manual" : mode === "filter" ? "Filter" : "CSV Upload"}
            </button>
          ))}
        </div>

        {targetMode === "manual" && (
          <>
            {/* Search */}
            <div className="relative mb-3">
              <div className="flex items-center gap-2 border border-surface-border rounded-lg px-3 py-2.5 bg-surface-bg focus-within:border-brand focus-within:ring-1 focus-within:ring-brand/20 transition-all">
                <FaSearch size={11} className="text-ink-faint" />
                <input
                  type="text"
                  className="flex-1 bg-transparent outline-none text-sm text-ink-primary placeholder:text-ink-faint"
                  placeholder="Search leads..."
                  value={leadSearch}
                  onChange={(e) => setLeadSearch(e.target.value)}
                />
                {searchingLeads && <Spinner size="sm" />}
              </div>
              {leadResults.length > 0 && (
                <div className="absolute z-10 mt-1 w-full bg-surface-card border border-surface-border rounded-xl shadow-xl py-1 max-h-52 overflow-y-auto">
                  {leadResults.map((lead) => {
                    const alreadyAdded = (form.leads || []).find((l) => l.id === lead.id);
                    return (
                      <button
                        key={lead.id}
                        type="button"
                        className={`w-full text-left px-3 py-2 hover:bg-brand/5 flex items-center justify-between transition-colors ${alreadyAdded ? "opacity-40" : ""}`}
                        onClick={() => !alreadyAdded && addLead(lead)}
                        disabled={!!alreadyAdded}
                      >
                        <div>
                          <p className="text-xs font-medium text-ink-primary">
                            {[lead.firstName, lead.lastName].filter(Boolean).join(" ") || lead.name || lead.phone || lead.id}
                          </p>
                          <p className="text-2xs text-ink-muted">
                            {lead.phone || ""} {lead.email ? `/ ${lead.email}` : ""}
                          </p>
                        </div>
                        {alreadyAdded && <Badge variant="neutral">Added</Badge>}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Assigned list */}
            {(form.leads || []).length > 0 ? (
              <div className="space-y-1.5 max-h-48 overflow-y-auto">
                {form.leads.map((lead) => (
                  <div
                    key={lead.id || lead}
                    className="flex items-center justify-between px-3 py-2 rounded-lg border border-surface-border bg-surface-bg group/lead"
                  >
                    <div className="min-w-0">
                      <p className="text-xs font-medium text-ink-primary truncate">
                        {[lead.firstName, lead.lastName].filter(Boolean).join(" ") || lead.name || lead.phone || lead.id}
                      </p>
                      {lead.phone && <p className="text-2xs text-ink-muted">{lead.phone}</p>}
                    </div>
                    <button
                      type="button"
                      className="text-ink-faint hover:text-red-500 p-1 opacity-0 group-hover/lead:opacity-100 transition-opacity"
                      onClick={() => removeLead(lead.id)}
                    >
                      <FaTimes size={9} />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6 border border-dashed border-surface-border rounded-lg">
                <FaUsers size={16} className="mx-auto text-ink-faint mb-2" />
                <p className="text-xs text-ink-muted">Search above to add leads</p>
              </div>
            )}
            <p className="text-2xs text-ink-muted mt-2">{(form.leads || []).length} lead{(form.leads || []).length !== 1 ? "s" : ""} selected</p>
          </>
        )}

        {targetMode === "filter" && (
          <>
            {/* Tag input */}
            <div className="input-group mb-4">
              <label className="input-label">Tags</label>
              <div className="flex items-center gap-2">
                <input
                  className="input flex-1"
                  placeholder="Type a tag and press Enter"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") { e.preventDefault(); addTag(tagInput); }
                  }}
                />
              </div>
              {(form.targetFilters?.tags || []).length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {form.targetFilters.tags.map((tag) => (
                    <span
                      key={tag}
                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-2xs font-medium bg-brand/10 text-brand"
                    >
                      {tag}
                      <button
                        type="button"
                        className="hover:text-red-500"
                        onClick={() => removeTag(tag)}
                      >
                        <FaTimes size={7} />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Stage checkboxes */}
            <div className="input-group mb-4">
              <label className="input-label">Stages</label>
              <div className="flex flex-wrap gap-2">
                {STAGE_OPTIONS.map((stage) => {
                  const checked = (form.targetFilters?.stages || []).includes(stage);
                  return (
                    <label
                      key={stage}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-colors text-xs capitalize ${
                        checked
                          ? "bg-brand/5 border border-brand/20 text-brand font-medium"
                          : "bg-surface-bg border border-surface-border text-ink-muted hover:text-ink-primary"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleStage(stage)}
                        className="accent-brand"
                      />
                      {stage}
                    </label>
                  );
                })}
              </div>
            </div>

            {/* Preview */}
            <div className="flex items-center gap-3">
              <button
                type="button"
                className="btn-secondary btn-sm flex items-center gap-1.5"
                onClick={previewFilters}
                disabled={previewing}
              >
                {previewing ? <Spinner size="sm" /> : <FaFilter size={9} />}
                Preview
              </button>
              {filterPreview && (
                <span className="text-xs text-ink-primary font-medium">
                  {filterPreview.total ?? 0} lead{(filterPreview.total ?? 0) !== 1 ? "s" : ""} match
                </span>
              )}
            </div>
          </>
        )}

        {targetMode === "csv" && (
          <>
            {/* File upload */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <label className="input-label mb-0">Upload CSV or Excel file</label>
                <button
                  type="button"
                  className="text-xs text-brand hover:text-brand-700 font-medium underline-offset-2 hover:underline"
                  onClick={() => downloadSampleCsv("contacts")}
                >
                  Download sample CSV
                </button>
              </div>
              <label className="flex flex-col items-center justify-center py-8 border-2 border-dashed border-surface-border rounded-xl cursor-pointer hover:border-brand/30 hover:bg-brand/5 transition-all">
                <FaUpload size={20} className="text-ink-faint mb-2" />
                <span className="text-xs font-medium text-ink-muted">
                  {csvFileName || "Click to select .csv, .xlsx, or .xls file"}
                </span>
                <input
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  className="hidden"
                  onChange={handleCsvFile}
                />
              </label>
              <p className="text-2xs text-ink-faint mt-2">
                Required column: <code className="text-brand">phone</code>. Optional built-ins: firstName, lastName, email, companyName, tags. Any other header maps to <code className="text-brand">Custom variable</code> by default — use it as <code className="text-brand">{"{{HeaderName}}"}</code> in your messages.
              </p>
            </div>

            {/* Column mapping with inline samples */}
            {csvRawRows.length > 0 && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <FaCheck size={11} className="text-green-500" />
                    <span className="text-xs font-medium text-green-600">File processed</span>
                    <span className="text-2xs text-ink-faint">
                      · {csvRawRows.length - 1} row{csvRawRows.length - 1 !== 1 ? "s" : ""}
                    </span>
                  </div>
                  <button
                    type="button"
                    className="text-xs text-red-500 hover:text-red-600 font-medium inline-flex items-center gap-1"
                    onClick={() => {
                      setCsvRawRows([]);
                      setCsvMapping([]);
                      setCsvFileName("");
                      setForm((p) => ({ ...p, csvLeads: [] }));
                    }}
                  >
                    <FaTrash size={10} /> Clear list
                  </button>
                </div>

                <div className="border border-surface-border rounded-lg overflow-hidden">
                  <div className="grid grid-cols-[1fr_1.4fr_1.2fr] gap-3 px-3 py-2 bg-surface-bg border-b border-surface-border">
                    <span className="text-2xs font-semibold text-ink-muted uppercase tracking-wider">Column Name</span>
                    <span className="text-2xs font-semibold text-ink-muted uppercase tracking-wider">Select Type</span>
                    <span className="text-2xs font-semibold text-ink-muted uppercase tracking-wider">Samples</span>
                  </div>
                  <div className="divide-y divide-surface-border max-h-80 overflow-y-auto">
                    {(csvRawRows[0] || []).map((header, ci) => {
                      const mapped = csvMapping[ci] || "skip";
                      const headerKey = String(header || "").trim();
                      const samples = csvRawRows
                        .slice(1, 4)
                        .map((r) => (r[ci] != null ? String(r[ci]) : ""))
                        .filter((v) => v !== "");
                      const meta = CSV_TYPE_META[mapped] || CSV_TYPE_META.skip;
                      const Icon = meta.Icon;
                      return (
                        <div key={ci} className="grid grid-cols-[1fr_1.4fr_1.2fr] gap-3 items-center px-3 py-2.5">
                          <div className="min-w-0">
                            <span className="text-xs text-ink-primary font-medium truncate block" title={header}>
                              {header || `Column ${ci + 1}`}
                            </span>
                            {mapped === "custom" && headerKey ? (
                              <code className="block text-2xs font-mono text-brand mt-0.5 truncate">
                                {`{{${headerKey}}}`}
                              </code>
                            ) : null}
                          </div>
                          <div className="flex items-center gap-2 min-w-0">
                            <span className={`w-6 h-6 rounded flex items-center justify-center flex-shrink-0 ${meta.bg}`}>
                              <Icon size={10} className={meta.color} />
                            </span>
                            <select
                              className="input py-1.5 text-xs flex-1 min-w-0"
                              value={mapped}
                              onChange={(e) => {
                                setCsvMapping((prev) => {
                                  const next = [...prev];
                                  next[ci] = e.target.value;
                                  return next;
                                });
                              }}
                            >
                              {CSV_MAPPING_OPTIONS.map((opt) => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                              ))}
                            </select>
                          </div>
                          <div className="flex flex-col text-2xs leading-tight min-w-0">
                            {samples.length > 0 ? (
                              samples.map((s, si) => (
                                <span
                                  key={si}
                                  className={`truncate ${si < 2 ? "text-brand" : "text-ink-primary"}`}
                                  title={s}
                                >
                                  {s}
                                </span>
                              ))
                            ) : (
                              <span className="text-ink-faint italic">empty</span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {!csvMapping.includes("phone") && (
                  <p className="text-2xs text-red-500">Phone column mapping is required.</p>
                )}

                {csvMapping.includes("phone") && (
                  <p className="text-2xs text-ink-muted">
                    {(form.csvLeads || []).length} valid lead{(form.csvLeads || []).length !== 1 ? "s" : ""} parsed from {csvRawRows.length - 1} row{csvRawRows.length - 1 !== 1 ? "s" : ""}
                  </p>
                )}

                {(form.csvLeads || []).length === 0 && csvMapping.includes("phone") && (
                  <div className="rounded-md border border-amber-300 bg-amber-50 p-2.5">
                    <p className="text-2xs font-semibold text-amber-800 mb-0.5">No valid leads parsed</p>
                    <p className="text-2xs text-amber-700 leading-relaxed">
                      Every row failed parsing. Common causes: blank phone column, Excel scientific notation
                      (9.19E+11), or non-numeric characters in the phone field. Match the layout in the
                      {" "}
                      <button type="button" onClick={() => downloadSampleCsv("outreach")} className="underline font-semibold">sample CSV</button>
                      {" "}and re-upload.
                    </p>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </CardBody>
    </Card>
  );
}

/* ── Helper: normalize legacy delayDays to delayValue/delayUnit ── */
function normalizeDelay(msg) {
  if (msg.delayValue !== undefined) return msg;
  return { ...msg, delayValue: msg.delayDays ?? 1, delayUnit: "days" };
}

function formatDelayBadge(msg, index) {
  if (index === 0) return "Now";
  const v = msg.delayValue ?? 0;
  const u = msg.delayUnit || "days";
  if (u === "minutes") return `${v}m`;
  if (u === "hours") return `${v}h`;
  return `Day ${v}`;
}

function formatDelayLabel(msg, index) {
  const v = msg.delayValue ?? 0;
  const u = msg.delayUnit || "minutes";
  const plural = v !== 1;
  const unitLabel = u === "minutes" ? `minute${plural ? "s" : ""}` : u === "hours" ? `hour${plural ? "s" : ""}` : `day${plural ? "s" : ""}`;
  if (index === 0) {
    return v === 0 ? "Sent immediately on activation" : `${v} ${unitLabel} after activation`;
  }
  return v === 0 ? "Sent immediately after previous" : `${v} ${unitLabel} after previous message`;
}

const DELAY_UNITS = [
  { value: "minutes", label: "Minutes" },
  { value: "hours", label: "Hours" },
  { value: "days", label: "Days" },
];

function hasMessageContent(message = {}) {
  return Boolean(
    String(message.text || "").trim() ||
    String(message.mediaUrl || "").trim() ||
    ((message.sendMode === "template" || message.kind === "template") && message.template?.name)
  );
}

function approvedCloudTemplates(sessions = [], sessionIds = [], templatesBySession = {}) {
  const cloudIds = (sessionIds || []).filter((sid) => {
    const session = sessions.find((s) => s.sessionId === sid);
    return session?.transportType === "walytic_cloud";
  });
  if (cloudIds.length === 0) return [];

  const counts = new Map();
  const firstByKey = new Map();
  cloudIds.forEach((sid) => {
    const seenForSession = new Set();
    (templatesBySession[sid] || [])
      .filter((template) => template.status === "APPROVED")
      .forEach((template) => {
        const key = `${template.name}:${template.language || "en_US"}`;
        if (seenForSession.has(key)) return;
        seenForSession.add(key);
        counts.set(key, (counts.get(key) || 0) + 1);
        if (!firstByKey.has(key)) firstByKey.set(key, template);
      });
  });

  return Array.from(firstByKey.entries())
    .filter(([key]) => counts.get(key) === cloudIds.length)
    .map(([, template]) => template);
}

function CloudSendControls({ msg, sessions, sessionIds, templatesBySession, onChange }) {
  const hasCloudSession = (sessionIds || []).some((sid) => {
    const session = sessions.find((s) => s.sessionId === sid);
    return session?.transportType === "walytic_cloud";
  });
  if (!hasCloudSession) return null;

  const templates = approvedCloudTemplates(sessions, sessionIds, templatesBySession);
  const templateValue = msg.template?.name
    ? `${msg.template.name}:${msg.template.language || "en_US"}`
    : "";

  return (
    <div className="grid sm:grid-cols-2 gap-2">
      <div>
        <label className="text-2xs font-medium text-ink-muted uppercase tracking-wider block mb-1.5">Cloud Send Mode</label>
        <select
          className="input text-sm"
          value={msg.sendMode || "auto"}
          onChange={(e) => {
            const sendMode = e.target.value;
            onChange({
              sendMode,
              kind: sendMode === "template" ? "template" : undefined,
              template: sendMode === "template" ? msg.template || null : null,
            });
          }}
        >
          <option value="auto">Auto</option>
          <option value="freeform">Free-form inside 24h</option>
          <option value="template">Approved template</option>
        </select>
      </div>
      {(msg.sendMode === "template" || msg.kind === "template") && (
        <div>
          <label className="text-2xs font-medium text-ink-muted uppercase tracking-wider block mb-1.5">Template</label>
          <select
            className="input text-sm"
            value={templateValue}
            onChange={(e) => {
              const [name, language] = e.target.value.split(":");
              const template = templates.find((tpl) => tpl.name === name && (tpl.language || "en_US") === language);
              onChange({
                sendMode: "template",
                kind: "template",
                template: template ? { name: template.name, language: template.language || "en_US", components: template.components || [] } : null,
              });
            }}
          >
            <option value="">Choose approved template</option>
            {templates.map((tpl) => (
              <option key={tpl.id || `${tpl.name}:${tpl.language}`} value={`${tpl.name}:${tpl.language || "en_US"}`}>
                {tpl.name} ({tpl.language || "en_US"})
              </option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
}

/* ════════════════════════════════════════════════
   Wizard Step 3 - Messages
   ════════════════════════════════════════════════ */
function WizardStep3({ form, setForm, sessions = [], templatesBySession = {} }) {
  // Normalize legacy messages on mount
  useEffect(() => {
    setForm((prev) => {
      const msgs = (prev.messages || [{ text: "", delayDays: 1 }]);
      const normalized = msgs.map(normalizeDelay);
      const changed = msgs.some((m, i) => m !== normalized[i]);
      return changed ? { ...prev, messages: normalized } : prev;
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const messages = (form.messages || [{ text: "", delayValue: 1, delayUnit: "days" }]).map(normalizeDelay);

  const updateMessage = (index, key, value) => {
    const patch = typeof key === "object" ? key : { [key]: value };
    setForm((prev) => ({
      ...prev,
      messages: prev.messages.map((m, i) => (i === index ? { ...m, ...patch } : m)),
    }));
  };

  const addMessage = () => {
    setForm((prev) => ({
      ...prev,
      messages: [...(prev.messages || []), { text: "", delayValue: 1, delayUnit: "days" }],
    }));
  };

  const removeMessage = (index) => {
    setForm((prev) => ({
      ...prev,
      messages: prev.messages.filter((_, i) => i !== index),
    }));
  };

  const insertVariable = (varKey, index) => {
    setForm((prev) => ({
      ...prev,
      messages: prev.messages.map((m, i) =>
        i === index ? { ...m, text: (m.text || "") + varKey } : m
      ),
    }));
  };

  const extraVars = useMemo(
    () => collectExtraVars(form.csvLeads),
    [form.csvLeads]
  );

  return (
    <Card>
      <CardBody className="p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-blue-500 flex items-center justify-center">
              <FaEnvelope size={13} className="text-white" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-ink-primary">Message Sequence</h3>
              <p className="text-2xs text-ink-muted mt-0.5">Build your drip timeline</p>
            </div>
          </div>
          <button
            type="button"
            className="btn-secondary btn-sm flex items-center gap-1.5"
            onClick={addMessage}
          >
            <FaPlus size={9} /> Add Step
          </button>
        </div>

        {/* Timeline */}
        <div className="relative w-full">
          {messages.map((msg, index) => (
            <div key={index} className="relative flex gap-2 sm:gap-4 pb-6 last:pb-0">
              {/* Step badge + vertical line */}
              <div className="hidden sm:flex flex-col items-center">
                <StepBadge number={index + 1} color={index === 0 ? "bg-green-500" : "bg-blue-500"} />
                {index < messages.length - 1 && (
                  <div className="w-0.5 flex-1 bg-surface-border mt-2" />
                )}
              </div>
              {/* Mobile step badge (smaller) */}
              <div className="flex sm:hidden flex-col items-center">
                <div className={`w-5 h-5 rounded-md ${index === 0 ? "bg-green-500" : "bg-blue-500"} flex items-center justify-center flex-shrink-0`}>
                  <span className="text-2xs font-bold text-white">{index + 1}</span>
                </div>
                {index < messages.length - 1 && (
                  <div className="w-0.5 flex-1 bg-surface-border mt-1" />
                )}
              </div>

                <div className="flex-1 rounded-xl border border-surface-border bg-surface-card hover:border-brand/20 transition-colors p-3 sm:p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-ink-primary">
                      {index === 0 ? "First Message" : `Follow-up ${index}`}
                    </span>
                    <Badge variant="neutral">
                      <FaClock size={8} className="mr-1" />
                      {formatDelayBadge(msg, index)}
                    </Badge>
                  </div>
                  {messages.length > 1 && (
                    <button
                      type="button"
                      className="text-ink-faint hover:text-red-500 p-1.5 rounded-lg hover:bg-red-50 transition-colors"
                      onClick={() => removeMessage(index)}
                      title="Remove message"
                    >
                      <FaTrash size={9} />
                    </button>
                  )}
                </div>

                <CloudSendControls
                  msg={msg}
                  sessions={sessions}
                  sessionIds={form.sessionIds}
                  templatesBySession={templatesBySession}
                  onChange={(patch) => updateMessage(index, patch)}
                />

                {/* Message textarea */}
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="text-2xs font-medium text-ink-muted uppercase tracking-wider">Message</label>
                    <VariableHelper onInsert={(v) => insertVariable(v, index)} extraVars={extraVars} />
                  </div>
                  <textarea
                    className="input min-h-[100px] sm:min-h-[100px] min-h-[130px] text-sm leading-relaxed"
                    placeholder="Type your message... {Hi|Hello|Hey} {{firstName}} for spintax + variables."
                    value={msg.text || ""}
                    onChange={(e) => updateMessage(index, "text", e.target.value)}
                  />
                  <SpintaxHelp />
                </div>

                {/* Media URL */}
                <div>
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <FaImage size={9} className="text-ink-faint" />
                    <label className="text-2xs font-medium text-ink-muted uppercase tracking-wider">Media URL (optional)</label>
                  </div>
                  <input
                    className="input text-sm"
                    placeholder="https://example.com/image.jpg"
                    value={msg.mediaUrl || ""}
                    onChange={(e) => updateMessage(index, "mediaUrl", e.target.value)}
                  />
                </div>

                {/* Timing - stacks vertically on mobile */}
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 pt-2 border-t border-surface-border">
                  <div className="flex items-center gap-2">
                    <FaClock size={10} className="text-ink-faint" />
                    <label className="text-xs text-ink-muted whitespace-nowrap">
                      {index === 0 ? "Delay after activation" : "Delay"}
                    </label>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      className="input w-20 text-center py-1.5"
                      min={0}
                      value={msg.delayValue ?? 0}
                      onChange={(e) => updateMessage(index, "delayValue", Math.max(0, Number(e.target.value) || 0))}
                    />
                    <select
                      className="input w-28 py-1.5"
                      value={msg.delayUnit || "minutes"}
                      onChange={(e) => updateMessage(index, "delayUnit", e.target.value)}
                    >
                      {DELAY_UNITS.map((u) => (
                        <option key={u.value} value={u.value}>{u.label}</option>
                      ))}
                    </select>
                  </div>
                  <span className="text-2xs text-ink-faint">
                    {formatDelayLabel(msg, index)}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>

        <button
          type="button"
          className="mt-4 w-full py-3 border-2 border-dashed border-surface-border rounded-xl text-xs font-medium text-ink-muted hover:border-brand/30 hover:text-brand hover:bg-brand/5 transition-all flex items-center justify-center gap-1.5"
          onClick={addMessage}
        >
          <FaPlus size={9} /> Add another message step
        </button>
      </CardBody>
    </Card>
  );
}

/* ════════════════════════════════════════════════
   Wizard Step 4 - Review
   ════════════════════════════════════════════════ */
function WizardStep4({ form, setForm, creating, onCreate }) {
  const messages = form.messages || [];
  const manualCount = (form.leads || []).length;
  const csvCount = (form.csvLeads || []).length;
  const filterTagCount = (form.targetFilters?.tags || []).length;
  const filterStageCount = (form.targetFilters?.stages || []).length;
  const filterEmpty = filterTagCount === 0 && filterStageCount === 0;

  const leadCount = form.targetType === "manual"
    ? manualCount
    : form.targetType === "csv"
    ? csvCount
    : "filter-based";

  // Block create when the chosen target mode has no audience.
  const noLeads =
    (form.targetType === "manual" && manualCount === 0) ||
    (form.targetType === "csv" && csvCount === 0) ||
    (form.targetType === "filter" && filterEmpty);

  const noLeadsHint = form.targetType === "manual"
    ? "No leads selected. Go back to Step 2 and pick contacts, or switch to CSV upload / Filter mode."
    : form.targetType === "csv"
    ? "No valid leads parsed from your CSV. Upload a file with a Phone column (use the sample CSV as a template), or switch target mode."
    : "Filter mode needs at least one tag or stage. Go back to Step 2 and pick filters, or switch to Manual / CSV mode.";

  return (
    <Card>
      <CardBody className="p-5">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-8 h-8 rounded-lg bg-purple-500 flex items-center justify-center">
            <FaRocket size={13} className="text-white" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-ink-primary">Review and Create</h3>
            <p className="text-2xs text-ink-muted mt-0.5">Confirm your campaign details</p>
          </div>
        </div>

        <div className="space-y-4 mb-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            <div className="p-3 rounded-lg bg-surface-bg border border-surface-border">
              <p className="text-2xs text-ink-muted uppercase tracking-wider mb-1">Campaign Name</p>
              <p className="text-sm font-medium text-ink-primary">{form.name || "Untitled"}</p>
            </div>
            <div className="p-3 rounded-lg bg-surface-bg border border-surface-border">
              <p className="text-2xs text-ink-muted uppercase tracking-wider mb-1">Sessions</p>
              <p className="text-sm font-medium text-ink-primary">{(form.sessionIds || []).length} session{(form.sessionIds || []).length !== 1 ? "s" : ""}</p>
            </div>
            <div className="p-3 rounded-lg bg-surface-bg border border-surface-border">
              <p className="text-2xs text-ink-muted uppercase tracking-wider mb-1">Leads</p>
              <p className="text-sm font-medium text-ink-primary">{leadCount}</p>
            </div>
            <div className="p-3 rounded-lg bg-surface-bg border border-surface-border">
              <p className="text-2xs text-ink-muted uppercase tracking-wider mb-1">Messages</p>
              <p className="text-sm font-medium text-ink-primary">{messages.length} message{messages.length !== 1 ? "s" : ""}</p>
            </div>
          </div>

          {/* Duration */}
          <div className="p-3 rounded-lg bg-surface-bg border border-surface-border">
            <p className="text-2xs text-ink-muted uppercase tracking-wider mb-1">Duration</p>
            <p className="text-sm font-medium text-ink-primary capitalize">{form.duration}</p>
          </div>
        </div>

        {/* Activate toggle */}
        <div className="flex items-center justify-between p-3 rounded-lg border border-surface-border mb-6">
          <div>
            <p className="text-sm font-medium text-ink-primary">Activate after creation</p>
            <p className="text-2xs text-ink-muted">You can also activate it later from the list</p>
          </div>
          <button
            type="button"
            className={`text-2xl transition-colors ${form.activateOnCreate ? "text-green-500" : "text-ink-faint"}`}
            onClick={() => setForm((p) => ({ ...p, activateOnCreate: !p.activateOnCreate }))}
          >
            {form.activateOnCreate ? <FaToggleOn /> : <FaToggleOff />}
          </button>
        </div>

        {noLeads && (
          <div className="mb-3 rounded-lg border border-amber-300 bg-amber-50 p-3">
            <p className="text-xs font-semibold text-amber-800 mb-1">No leads will be enrolled</p>
            <p className="text-2xs text-amber-700 leading-relaxed">{noLeadsHint}</p>
            {form.targetType === "csv" && (
              <button
                type="button"
                onClick={() => downloadSampleCsv("outreach")}
                className="mt-2 text-2xs font-semibold text-amber-800 underline hover:text-amber-900"
              >
                Download sample CSV
              </button>
            )}
          </div>
        )}

        <button
          type="button"
          className="btn-primary w-full flex items-center justify-center gap-2 py-3"
          disabled={creating || !form.name.trim() || (form.sessionIds || []).length === 0 || noLeads}
          onClick={onCreate}
        >
          {creating ? <Spinner size="sm" /> : <FaRocket size={12} />}
          {creating ? "Creating..." : noLeads ? "Add leads to continue" : "Create Campaign"}
        </button>
      </CardBody>
    </Card>
  );
}

/* ════════════════════════════════════════════════
   Campaign Detail View
   ════════════════════════════════════════════════ */
/* ── Add Leads Modal (CRM picker + CSV paste/upload) ── */
function AddLeadsModal({ campaignId, onClose, onSuccess }) {
  const modal = useModal();
  const [tab, setTab] = useState("crm"); // "crm" | "csv"

  // CRM tab state
  const [contacts, setContacts] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [contactSearch, setContactSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  // CSV tab state
  const [csvText, setCsvText] = useState("");
  const [csvFileName, setCsvFileName] = useState("");
  const [parsedRows, setParsedRows] = useState([]);

  const [submitting, setSubmitting] = useState(false);

  // Fetch contacts on mount when CRM tab opens
  useEffect(() => {
    if (tab !== "crm") return;
    let cancelled = false;
    setContactsLoading(true);
    apiGet("/api/contacts?isLead=true&limit=200")
      .then((res) => {
        if (cancelled) return;
        setContacts(res?.contacts || res?.leads || res?.data || []);
      })
      .catch(() => { if (!cancelled) setContacts([]); })
      .finally(() => { if (!cancelled) setContactsLoading(false); });
    return () => { cancelled = true; };
  }, [tab]);

  const filteredContacts = contacts.filter((c) => {
    if (!contactSearch) return true;
    const q = contactSearch.toLowerCase();
    const name = ((c.firstName || "") + " " + (c.lastName || "")).toLowerCase();
    return (
      name.includes(q) ||
      (c.phone || "").toLowerCase().includes(q) ||
      (c.email || "").toLowerCase().includes(q)
    );
  });

  const toggleContact = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAllVisible = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      const allSelected = filteredContacts.every((c) => next.has(c.id));
      if (allSelected) {
        filteredContacts.forEach((c) => next.delete(c.id));
      } else {
        filteredContacts.forEach((c) => next.add(c.id));
      }
      return next;
    });
  };

  // ── CSV parsing helpers ──
  const parsePastedRows = useCallback((text) => {
    if (!text || !text.trim()) return [];
    const rawRows = parseCSVText(text);
    if (rawRows.length === 0) return [];
    // Detect header row: if first row's first cell isn't a phone-ish string
    const firstCell = String(rawRows[0]?.[0] || "");
    const looksLikeHeader = /[a-z]/i.test(firstCell) && !/\+?\d{6,}/.test(firstCell);
    const dataRows = looksLikeHeader ? rawRows.slice(1) : rawRows;
    const out = [];
    for (const row of dataRows) {
      const phoneRaw = row[0];
      if (!phoneRaw) continue;
      const phone = normalizePhoneFromImport(phoneRaw);
      if (!phone) continue;
      const lead = { phone };
      if (row[1]) lead.firstName = String(row[1]).trim();
      if (row[2]) lead.lastName = String(row[2]).trim();
      out.push(lead);
    }
    return out;
  }, []);

  useEffect(() => {
    if (tab !== "csv") return;
    setParsedRows(parsePastedRows(csvText));
  }, [csvText, tab, parsePastedRows]);

  const handleCsvFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCsvFileName(file.name);
    try {
      const text = await file.text();
      setCsvText(text);
    } catch {
      modal.alert("Could not read the file.", "error");
    }
  };

  // ── Submit handlers ──
  const renderEnrolledSummary = (enrolled, skipped) => {
    return `Added ${enrolled} lead${enrolled === 1 ? "" : "s"}. ${skipped} skipped (duplicates / invalid).`;
  };

  const submitCrm = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) {
      modal.alert("Select at least one contact.", "warning");
      return;
    }
    setSubmitting(true);
    try {
      const res = await apiPost(`/api/outreach/${campaignId}/assign`, {
        contactIds: ids,
      });
      if (res?.success) {
        const results = Array.isArray(res.data) ? res.data : [];
        const enrolled = results.filter((r) => r.status === "assigned").length;
        const skipped = results.length - enrolled;
        modal.alert(renderEnrolledSummary(enrolled, skipped), skipped > 0 ? "warning" : "success");
        onSuccess?.();
      } else {
        modal.alert(res?.message || "Failed to add leads", "error");
      }
    } catch (err) {
      modal.alert(err?.message || "Failed to add leads", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const submitCsv = async () => {
    if (parsedRows.length === 0) {
      modal.alert("No valid rows parsed. Each line needs at least a phone number with country code.", "warning");
      return;
    }
    setSubmitting(true);
    try {
      const res = await apiPost(`/api/outreach/${campaignId}/assign-csv`, {
        rows: parsedRows,
      });
      if (res?.success) {
        const enrolled = res.enrolled ?? 0;
        const skipped = res.skipped ?? Math.max(0, (res.total ?? parsedRows.length) - enrolled);
        modal.alert(renderEnrolledSummary(enrolled, skipped), skipped > 0 ? "warning" : "success");
        onSuccess?.();
      } else {
        modal.alert(res?.message || "Failed to add leads", "error");
      }
    } catch (err) {
      modal.alert(err?.message || "Failed to add leads", "error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-surface-card rounded-xl shadow-2xl w-[640px] max-w-[95vw] max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-surface-border px-4 py-3">
          <h2 className="text-base font-semibold text-ink-primary">Add Leads</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-ink-muted hover:text-ink-primary"
            disabled={submitting}
          >
            <FaTimes />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 px-4 pt-3 border-b border-surface-border">
          <button
            type="button"
            className={`px-3 py-2 text-xs font-medium border-b-2 transition-colors ${
              tab === "crm"
                ? "border-brand text-brand"
                : "border-transparent text-ink-muted hover:text-ink-primary"
            }`}
            onClick={() => setTab("crm")}
          >
            From CRM Contacts
          </button>
          <button
            type="button"
            className={`px-3 py-2 text-xs font-medium border-b-2 transition-colors ${
              tab === "csv"
                ? "border-brand text-brand"
                : "border-transparent text-ink-muted hover:text-ink-primary"
            }`}
            onClick={() => setTab("csv")}
          >
            Paste / Upload CSV
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4">
          {tab === "crm" ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <FaSearch size={10} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-faint" />
                  <input
                    type="text"
                    value={contactSearch}
                    onChange={(e) => setContactSearch(e.target.value)}
                    placeholder="Search by name, phone, or email"
                    className="w-full border border-surface-border rounded-lg pl-7 pr-3 py-2 text-sm outline-none focus:border-brand/40"
                  />
                </div>
                <button
                  type="button"
                  className="btn-ghost btn-sm"
                  onClick={toggleAllVisible}
                  disabled={filteredContacts.length === 0}
                >
                  {filteredContacts.length > 0 && filteredContacts.every((c) => selectedIds.has(c.id))
                    ? "Unselect all"
                    : "Select all"}
                </button>
              </div>

              <div className="border border-surface-border rounded-lg max-h-[340px] overflow-y-auto">
                {contactsLoading ? (
                  <div className="flex items-center justify-center py-8"><Spinner size="md" /></div>
                ) : filteredContacts.length === 0 ? (
                  <div className="py-8">
                    <EmptyState
                      icon={FaUsers}
                      title="No contacts found"
                      description={contacts.length === 0
                        ? "You don't have any leads in your CRM yet."
                        : "No contacts match your search."}
                    />
                  </div>
                ) : (
                  filteredContacts.map((c) => {
                    const checked = selectedIds.has(c.id);
                    const name = `${c.firstName || ""} ${c.lastName || ""}`.trim() || "Unnamed";
                    return (
                      <label
                        key={c.id}
                        className={`flex items-center gap-3 px-3 py-2 cursor-pointer border-b border-surface-border last:border-b-0 transition-colors ${
                          checked ? "bg-brand/5" : "hover:bg-surface-bg"
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleContact(c.id)}
                          className="accent-brand"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-ink-primary truncate">{name}</p>
                          <p className="text-2xs text-ink-muted truncate">
                            {c.phone || "no phone"}{c.email ? ` • ${c.email}` : ""}
                          </p>
                        </div>
                        {c.stage && <Badge variant="neutral">{c.stage}</Badge>}
                      </label>
                    );
                  })
                )}
              </div>

              <p className="text-2xs text-ink-faint">
                {selectedIds.size} contact{selectedIds.size === 1 ? "" : "s"} selected
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-ink-secondary mb-1">
                  Paste rows (phone, first name, last name)
                </label>
                <textarea
                  rows={6}
                  value={csvText}
                  onChange={(e) => setCsvText(e.target.value)}
                  placeholder={"+919876543210, John, Doe\n+14155550100, Jane, Smith"}
                  className="w-full border border-surface-border rounded-lg px-3 py-2 text-sm font-mono outline-none focus:border-brand/40"
                />
                <p className="text-2xs text-ink-faint mt-1">
                  One lead per line. First column is the phone (with country code). Header row optional.
                </p>
              </div>

              <div className="flex items-center gap-2">
                <label className="btn-secondary btn-sm flex items-center gap-1.5 cursor-pointer">
                  <FaUpload size={9} /> Upload CSV
                  <input
                    type="file"
                    accept=".csv,.txt"
                    onChange={handleCsvFile}
                    className="hidden"
                  />
                </label>
                {csvFileName && (
                  <span className="text-2xs text-ink-muted truncate">{csvFileName}</span>
                )}
              </div>

              {parsedRows.length > 0 && (
                <div>
                  <p className="text-2xs text-ink-faint mb-1">
                    Preview ({parsedRows.length} valid row{parsedRows.length === 1 ? "" : "s"}, showing first 5)
                  </p>
                  <div className="border border-surface-border rounded-lg overflow-x-auto">
                    <table className="w-full text-xs min-w-[400px]">
                      <thead className="bg-surface-bg">
                        <tr>
                          <th className="px-3 py-1.5 text-left font-medium text-ink-secondary">Phone</th>
                          <th className="px-3 py-1.5 text-left font-medium text-ink-secondary">First name</th>
                          <th className="px-3 py-1.5 text-left font-medium text-ink-secondary">Last name</th>
                        </tr>
                      </thead>
                      <tbody>
                        {parsedRows.slice(0, 5).map((r, i) => (
                          <tr key={i} className="border-t border-surface-border">
                            <td className="px-3 py-1.5 font-mono text-ink-primary">{r.phone}</td>
                            <td className="px-3 py-1.5 text-ink-muted">{r.firstName || ""}</td>
                            <td className="px-3 py-1.5 text-ink-muted">{r.lastName || ""}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 border-t border-surface-border px-4 py-3">
          <button
            type="button"
            className="btn-secondary btn-sm"
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary btn-sm flex items-center gap-1.5"
            onClick={tab === "crm" ? submitCrm : submitCsv}
            disabled={
              submitting ||
              (tab === "crm" ? selectedIds.size === 0 : parsedRows.length === 0)
            }
          >
            {submitting ? <Spinner size="sm" /> : <FaUserPlus size={10} />}
            {submitting ? "Adding…" : "Add Leads"}
          </button>
        </div>
      </div>
    </div>
  );
}

function CampaignDetail({ campaignId, onBack }) {
  const modal = useModal();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionStats, setSessionStats] = useState([]);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({ name: "", sessionIds: [], messages: [], stopOnReply: true });
  const [editSessions, setEditSessions] = useState([]);
  const [editTemplatesBySession, setEditTemplatesBySession] = useState({});
  const [saving, setSaving] = useState(false);

  // Duplicate modal state
  const [showDuplicate, setShowDuplicate] = useState(false);
  const [dupName, setDupName] = useState("");
  const [dupWithLeads, setDupWithLeads] = useState(false);
  const [duplicating, setDuplicating] = useState(false);

  // Add Leads modal state
  const [showAddLeads, setShowAddLeads] = useState(false);

  const fetchDetail = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiGet(`/api/outreach/${campaignId}`);
      if (res?.success) setCampaign(res.data);
    } catch { /* noop */ }
    finally { setLoading(false); }
  }, [campaignId]);

  const fetchSessionStats = useCallback(async () => {
    try {
      const res = await apiGet(`/api/outreach/${campaignId}/stats`);
      setSessionStats(res?.data || []);
    } catch { setSessionStats([]); }
  }, [campaignId]);

  const fetchEditSessions = useCallback(async () => {
    try {
      const res = await apiGet("/api/sessions");
      setEditSessions((res?.data || []).filter((s) => s.status === "connected"));
    } catch { setEditSessions([]); }
  }, []);

  const fetchEditTemplatesForSessions = useCallback(async (sessionIds = []) => {
    const cloudIds = (sessionIds || []).filter((sid) => {
      const session = editSessions.find((s) => s.sessionId === sid);
      return session?.transportType === "walytic_cloud";
    });
    if (cloudIds.length === 0) {
      setEditTemplatesBySession({});
      return;
    }

    const entries = await Promise.all(cloudIds.map(async (sid) => {
      try {
        const res = await apiGet(`/api/cloud/sessions/${sid}/templates`);
        return [sid, res?.templates || []];
      } catch {
        return [sid, []];
      }
    }));
    setEditTemplatesBySession(Object.fromEntries(entries));
  }, [editSessions]);

  useEffect(() => {
    fetchDetail();
    fetchSessionStats();
  }, [fetchDetail, fetchSessionStats]);

  useEffect(() => {
    if (editing) fetchEditTemplatesForSessions(editForm.sessionIds || []);
  }, [editing, editForm.sessionIds, fetchEditTemplatesForSessions]);

  const startEditing = () => {
    if (!campaign) return;
    setEditForm({
      name: campaign.name || "",
      sessionIds: campaign.sessionIds || [],
      messages: (campaign.messages || []).map(normalizeDelay),
      stopOnReply: campaign.stopOnReply !== false,
    });
    fetchEditSessions();
    setEditing(true);
  };

  const cancelEditing = () => {
    setEditing(false);
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      const res = await apiPut(`/api/outreach/${campaignId}`, {
        name: editForm.name.trim(),
        sessionIds: editForm.sessionIds,
        messages: editForm.messages,
        stopOnReply: editForm.stopOnReply !== false,
      });
      if (res?.success) {
        modal.alert("Campaign updated!", "success");
        setEditing(false);
        fetchDetail();
      } else {
        modal.alert(res?.message || "Failed to save changes", "error");
      }
    } catch (err) {
      modal.alert(err?.message || "Failed to save changes", "error");
    } finally {
      setSaving(false);
    }
  };

  const editUpdateMessage = (index, key, value) => {
    const patch = typeof key === "object" ? key : { [key]: value };
    setEditForm((prev) => ({
      ...prev,
      messages: prev.messages.map((m, i) => (i === index ? { ...m, ...patch } : m)),
    }));
  };

  const editAddMessage = () => {
    setEditForm((prev) => ({
      ...prev,
      messages: [...prev.messages, { text: "", delayValue: 1, delayUnit: "days" }],
    }));
  };

  const editRemoveMessage = (index) => {
    setEditForm((prev) => ({
      ...prev,
      messages: prev.messages.filter((_, i) => i !== index),
    }));
  };

  const editInsertVariable = (varKey, index) => {
    setEditForm((prev) => ({
      ...prev,
      messages: prev.messages.map((m, i) =>
        i === index ? { ...m, text: (m.text || "") + varKey } : m
      ),
    }));
  };

  const editToggleSession = (sid) => {
    setEditForm((prev) => {
      const current = prev.sessionIds || [];
      return {
        ...prev,
        sessionIds: current.includes(sid)
          ? current.filter((id) => id !== sid)
          : [...current, sid],
      };
    });
  };

  const handleToggle = async () => {
    const res = await apiPut(`/api/outreach/${campaignId}/toggle`);
    if (res?.success) fetchDetail();
    else modal.alert(res?.message || "Failed to toggle", "error");
  };

  const handleDelete = async () => {
    if (!(await modal.confirm(`Delete this campaign? This cannot be undone.`, { danger: true, confirmText: "Delete" }))) return;
    const res = await apiDelete(`/api/outreach/${campaignId}`);
    if (res?.success) onBack();
  };

  const handleUnassignLead = async (exec) => {
    const contactId = exec?.contactId || exec?.contact?.id;
    if (!contactId) {
      modal.alert("Could not determine contact id for this row.", "error");
      return;
    }
    const label = exec.contactName || exec.contact?.name || exec.phone || "this lead";
    const ok = await modal.confirm(
      `Remove ${label} from this campaign? Pending sends will be cancelled. The contact stays in your CRM.`,
      { danger: true, confirmText: "Remove" }
    );
    if (!ok) return;
    const res = await apiPost(`/api/outreach/${campaignId}/unassign`, { contactIds: [contactId] });
    if (res?.success) {
      fetchDetail();
    } else {
      modal.alert(res?.message || "Failed to remove lead", "error");
    }
  };

  const openDuplicateModal = () => {
    setDupName(`Copy of ${campaign?.name || ""}`);
    setDupWithLeads(false);
    setShowDuplicate(true);
  };

  const handleDuplicateConfirm = async () => {
    const name = dupName.trim();
    if (!name) {
      modal.alert("Please enter a name for the duplicated campaign.", "warning");
      return;
    }
    setDuplicating(true);
    try {
      const res = await apiPost(`/api/outreach/${campaignId}/duplicate`, {
        name,
        withLeads: dupWithLeads,
      });
      if (res?.success && res.data?.id) {
        setShowDuplicate(false);
        modal.alert(
          "Campaign duplicated. The copy is paused — review and activate when ready.",
          "success"
        );
        navigate(`/campaigns/${res.data.id}`);
      } else {
        modal.alert(res?.message || "Failed to duplicate campaign", "error");
      }
    } catch (err) {
      modal.alert(err?.message || "Failed to duplicate campaign", "error");
    } finally {
      setDuplicating(false);
    }
  };

  if (loading) {
    return (
      <div className="page-content">
        <div className="flex items-center justify-center py-16"><Spinner size="lg" /></div>
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="page-content">
        <button className="btn-ghost btn-sm flex items-center gap-1.5 mb-4" onClick={onBack}>
          <FaArrowLeft size={11} /> Back
        </button>
        <Card><CardBody className="py-16"><EmptyState icon={FaBullhorn} title="Campaign not found" /></CardBody></Card>
      </div>
    );
  }

  const stats = campaign.aggregateStats || {};
  const executions = campaign.executions || [];
  const pctDelivered = stats.sent > 0 ? Math.round((stats.delivered / stats.sent) * 100) : 0;
  const pctReplied = stats.sent > 0 ? Math.round((stats.replied / stats.sent) * 100) : 0;

  return (
    <div className="page-content">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 mb-6">
        <div className="flex items-center gap-3 min-w-0">
          <button className="btn-ghost btn-sm flex items-center gap-1.5 flex-shrink-0" onClick={onBack}>
            <FaArrowLeft size={11} /> Back
          </button>
          <div className="w-px h-5 bg-surface-border hidden sm:block" />
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-8 h-8 rounded-lg bg-brand flex items-center justify-center flex-shrink-0">
              <FaBullhorn size={12} className="text-white" />
            </div>
            <div className="min-w-0">
              <h2 className="text-sm font-semibold text-ink-primary truncate">{campaign.name}</h2>
              <div className="flex items-center flex-wrap gap-2 mt-0.5">
                <Badge variant={campaign.active ? "success" : "neutral"}>
                  {campaign.active ? "Active" : "Inactive"}
                </Badge>
                <button
                  type="button"
                  title="Click to copy — share this ID with support if the campaign isn't sending"
                  onClick={() => {
                    navigator.clipboard?.writeText(campaign.id);
                    modal.alert("Campaign ID copied. Share this with support if you need help.", "success");
                  }}
                  className="text-2xs font-mono text-ink-faint hover:text-brand transition-colors px-2 py-0.5 rounded bg-surface-bg border border-surface-border truncate max-w-[160px] sm:max-w-none"
                >
                  ID: {campaign.id}
                </button>
              </div>
            </div>
          </div>
        </div>
        <div className="flex items-center flex-wrap gap-2">
          <button
            className="btn-secondary btn-sm flex items-center gap-1"
            onClick={() => window.location.assign(`/campaigns/${campaign.id}/analytics`)}
            title="Live activity log + per-fail reasons"
          >
            <FaChartLine size={10} /> Analytics
          </button>
          <button
            className="btn-secondary btn-sm flex items-center gap-1"
            onClick={startEditing}
          >
            <FaEdit size={10} /> Edit
          </button>
          <button
            className="btn-secondary btn-sm flex items-center gap-1"
            onClick={openDuplicateModal}
            title="Duplicate this campaign"
          >
            <FaCopy size={10} /> Duplicate
          </button>
          <button
            className="btn-secondary btn-sm flex items-center gap-1"
            onClick={() => setShowAddLeads(true)}
            title="Add leads to this campaign"
          >
            <FaUserPlus size={10} /> Add Leads
          </button>
          <button
            className={`btn-sm flex items-center gap-1 ${campaign.active ? "btn-secondary" : "btn-primary"}`}
            onClick={handleToggle}
          >
            {campaign.active ? <><FaToggleOff size={11} /> Pause</> : <><FaToggleOn size={11} /> Activate</>}
          </button>
          <button className="btn-danger btn-sm btn-icon" onClick={handleDelete}>
            <FaTrash size={10} />
          </button>
        </div>
      </div>

      {/* Duplicate modal */}
      {showDuplicate && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center">
          <div className="bg-surface-card rounded-xl shadow-2xl w-[460px] max-w-[90vw]">
            <div className="flex items-center justify-between border-b border-surface-border px-4 py-3">
              <h2 className="text-base font-semibold text-ink-primary">Duplicate Campaign</h2>
              <button
                type="button"
                onClick={() => setShowDuplicate(false)}
                className="text-ink-muted hover:text-ink-primary"
                disabled={duplicating}
              >
                <FaTimes />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-xs font-medium text-ink-secondary mb-1">
                  New campaign name
                </label>
                <input
                  type="text"
                  value={dupName}
                  onChange={(e) => setDupName(e.target.value)}
                  className="w-full border border-surface-border rounded-lg px-3 py-2 text-sm outline-none focus:border-brand/40 transition-colors"
                  placeholder="Copy of My Campaign"
                  autoFocus
                />
              </div>
              <label className="flex items-start gap-2 text-sm cursor-pointer p-2 rounded-lg border border-surface-border hover:bg-surface-bg transition-colors">
                <input
                  type="checkbox"
                  className="mt-0.5 accent-brand"
                  checked={dupWithLeads}
                  onChange={(e) => setDupWithLeads(e.target.checked)}
                />
                <span>
                  <span className="font-medium text-ink-primary">Also copy the lead list</span>
                  <span className="text-2xs text-ink-faint block mt-0.5">
                    When checked, all enrolled contacts are added to the new campaign too. Otherwise the new campaign starts with no leads.
                  </span>
                </span>
              </label>
            </div>
            <div className="flex items-center justify-end gap-2 border-t border-surface-border px-4 py-3">
              <button
                type="button"
                className="btn-secondary btn-sm"
                onClick={() => setShowDuplicate(false)}
                disabled={duplicating}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-primary btn-sm flex items-center gap-1.5"
                onClick={handleDuplicateConfirm}
                disabled={duplicating || !dupName.trim()}
              >
                {duplicating ? <Spinner size="sm" /> : <FaCopy size={10} />}
                {duplicating ? "Duplicating…" : "Duplicate"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Leads modal */}
      {showAddLeads && (
        <AddLeadsModal
          campaignId={campaignId}
          onClose={() => setShowAddLeads(false)}
          onSuccess={() => {
            setShowAddLeads(false);
            fetchDetail();
          }}
        />
      )}

      {/* Edit mode */}
      {editing ? (
        <div className="space-y-6">
          {/* Campaign name */}
          <Card>
            <CardBody className="p-5">
              <div className="input-group">
                <label className="input-label">Campaign Name</label>
                <input
                  className="input"
                  value={editForm.name}
                  onChange={(e) => setEditForm((p) => ({ ...p, name: e.target.value }))}
                />
              </div>
            </CardBody>
          </Card>

          {/* Sessions */}
          <Card>
            <CardBody className="p-5">
              <label className="input-label mb-2 block">WhatsApp Sessions</label>
              {editSessions.length === 0 ? (
                <p className="input-hint text-amber-600">No connected sessions found.</p>
              ) : (
                <div className="space-y-2 max-h-48 overflow-y-auto border border-surface-border rounded-lg p-3">
                  {editSessions.map((s) => {
                    const checked = (editForm.sessionIds || []).includes(s.sessionId);
                    return (
                      <label
                        key={s.sessionId}
                        className={`flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer transition-colors ${
                          checked ? "bg-brand/5 border border-brand/20" : "hover:bg-surface-bg border border-transparent"
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => editToggleSession(s.sessionId)}
                          className="accent-brand"
                        />
                        <div>
                          <p className="text-xs font-medium text-ink-primary">
                            {s.number || s.sessionId}
                            {s.transportType === 'walytic_browser' && (
                              <span className="ml-1.5 px-1 py-0.5 rounded text-2xs bg-brand/10 text-brand">browser</span>
                            )}
                            {s.transportType === 'walytic_cloud' && (
                              <span className="ml-1.5 px-1 py-0.5 rounded text-2xs bg-blue-50 text-blue-600">cloud</span>
                            )}
                          </p>
                          {s.name && <p className="text-2xs text-ink-muted">{s.name}</p>}
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}
              <p className="input-hint mt-2">
                {(editForm.sessionIds || []).length} session{(editForm.sessionIds || []).length !== 1 ? "s" : ""} selected
              </p>
            </CardBody>
          </Card>

          {/* Reply behaviour */}
          <Card>
            <CardBody className="p-5">
              <label className="input-label mb-2 block">Reply Behaviour</label>
              <label className="flex items-start gap-2 text-sm cursor-pointer p-2 rounded-lg border border-surface-border hover:bg-surface-bg transition-colors">
                <input
                  type="checkbox"
                  className="mt-0.5 accent-brand"
                  checked={editForm.stopOnReply !== false}
                  onChange={(e) => setEditForm((p) => ({ ...p, stopOnReply: e.target.checked }))}
                />
                <span>
                  <span className="font-medium text-ink-primary">Stop sending follow-ups when a contact replies</span>
                  <span className="text-2xs text-ink-faint block mt-0.5">
                    On (default) = engagement mode. Off = nurture mode (drip continues regardless of replies).
                  </span>
                </span>
              </label>
            </CardBody>
          </Card>

          {/* Messages sequence editor */}
          <Card>
            <CardBody className="p-5">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-blue-500 flex items-center justify-center">
                    <FaEnvelope size={13} className="text-white" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-ink-primary">Message Sequence</h3>
                    <p className="text-2xs text-ink-muted mt-0.5">Edit your drip timeline</p>
                  </div>
                </div>
                <button
                  type="button"
                  className="btn-secondary btn-sm flex items-center gap-1.5"
                  onClick={editAddMessage}
                >
                  <FaPlus size={9} /> Add Step
                </button>
              </div>

              <div className="relative w-full">
                {(editForm.messages || []).map((msg, index) => {
                  const m = normalizeDelay(msg);
                  return (
                    <div key={index} className="relative flex gap-2 sm:gap-4 pb-6 last:pb-0">
                      <div className="hidden sm:flex flex-col items-center">
                        <StepBadge number={index + 1} color={index === 0 ? "bg-green-500" : "bg-blue-500"} />
                        {index < editForm.messages.length - 1 && (
                          <div className="w-0.5 flex-1 bg-surface-border mt-2" />
                        )}
                      </div>
                      <div className="flex sm:hidden flex-col items-center">
                        <div className={`w-5 h-5 rounded-md ${index === 0 ? "bg-green-500" : "bg-blue-500"} flex items-center justify-center flex-shrink-0`}>
                          <span className="text-2xs font-bold text-white">{index + 1}</span>
                        </div>
                        {index < editForm.messages.length - 1 && (
                          <div className="w-0.5 flex-1 bg-surface-border mt-1" />
                        )}
                      </div>

                      <div className="flex-1 rounded-xl border border-surface-border bg-surface-card hover:border-brand/20 transition-colors p-3 sm:p-4 space-y-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-semibold text-ink-primary">
                              {index === 0 ? "First Message" : `Follow-up ${index}`}
                            </span>
                            <Badge variant="neutral">
                              <FaClock size={8} className="mr-1" />
                              {formatDelayBadge(m, index)}
                            </Badge>
                          </div>
                          {editForm.messages.length > 1 && (
                            <button
                              type="button"
                              className="text-ink-faint hover:text-red-500 p-1.5 rounded-lg hover:bg-red-50 transition-colors"
                              onClick={() => editRemoveMessage(index)}
                            >
                              <FaTrash size={9} />
                            </button>
                          )}
                        </div>

                        <CloudSendControls
                          msg={m}
                          sessions={editSessions}
                          sessionIds={editForm.sessionIds}
                          templatesBySession={editTemplatesBySession}
                          onChange={(patch) => editUpdateMessage(index, patch)}
                        />

                        <div>
                          <div className="flex items-center justify-between mb-1.5">
                            <label className="text-2xs font-medium text-ink-muted uppercase tracking-wider">Message</label>
                            <VariableHelper onInsert={(v) => editInsertVariable(v, index)} />
                          </div>
                          <textarea
                            className="input min-h-[100px] sm:min-h-[100px] min-h-[130px] text-sm leading-relaxed"
                            placeholder="Type your message..."
                            value={m.text || ""}
                            onChange={(e) => editUpdateMessage(index, "text", e.target.value)}
                          />
                        </div>

                        <div>
                          <div className="flex items-center gap-1.5 mb-1.5">
                            <FaImage size={9} className="text-ink-faint" />
                            <label className="text-2xs font-medium text-ink-muted uppercase tracking-wider">Media URL (optional)</label>
                          </div>
                          <input
                            className="input text-sm"
                            placeholder="https://example.com/image.jpg"
                            value={m.mediaUrl || ""}
                            onChange={(e) => editUpdateMessage(index, "mediaUrl", e.target.value)}
                          />
                        </div>

                        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 pt-2 border-t border-surface-border">
                          <div className="flex items-center gap-2">
                            <FaClock size={10} className="text-ink-faint" />
                            <label className="text-xs text-ink-muted whitespace-nowrap">
                              {index === 0 ? "Delay after activation" : "Delay"}
                            </label>
                          </div>
                          <div className="flex items-center gap-2">
                            <input
                              type="number"
                              className="input w-20 text-center py-1.5"
                              min={0}
                              value={m.delayValue ?? 0}
                              onChange={(e) => editUpdateMessage(index, "delayValue", Math.max(0, Number(e.target.value) || 0))}
                            />
                            <select
                              className="input w-28 py-1.5"
                              value={m.delayUnit || "minutes"}
                              onChange={(e) => editUpdateMessage(index, "delayUnit", e.target.value)}
                            >
                              {DELAY_UNITS.map((u) => (
                                <option key={u.value} value={u.value}>{u.label}</option>
                              ))}
                            </select>
                          </div>
                          <span className="text-2xs text-ink-faint">
                            {formatDelayLabel(m, index)}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <button
                type="button"
                className="mt-4 w-full py-3 border-2 border-dashed border-surface-border rounded-xl text-xs font-medium text-ink-muted hover:border-brand/30 hover:text-brand hover:bg-brand/5 transition-all flex items-center justify-center gap-1.5"
                onClick={editAddMessage}
              >
                <FaPlus size={9} /> Add another message step
              </button>
            </CardBody>
          </Card>

          {/* Save / Cancel buttons */}
          <div className="flex items-center gap-3">
            <button
              className="btn-primary flex items-center gap-2"
              disabled={saving || !editForm.name.trim()}
              onClick={handleSaveEdit}
            >
              {saving ? <Spinner size="sm" /> : <FaSave size={11} />}
              {saving ? "Saving..." : "Save Changes"}
            </button>
            <button
              className="btn-secondary"
              onClick={cancelEditing}
              disabled={saving}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
      <>
      {/* Stats cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatBox
          label="Total Leads"
          value={campaign.leadCount ?? campaign.executions?.length ?? 0}
          icon={FaUsers}
          color="bg-indigo-500"
        />
        <StatBox label="Sent" value={stats.sent ?? 0} icon={FaPaperPlane} color="bg-blue-500" />
        <StatBox
          label="Delivered"
          value={stats.delivered ?? 0}
          sub={stats.sent > 0 ? `${pctDelivered}% of sent` : undefined}
          icon={FaCheck}
          color="bg-green-500"
        />
        <StatBox
          label="Replied"
          value={stats.replied ?? 0}
          sub={stats.sent > 0 ? `${pctReplied}% of sent` : undefined}
          icon={FaReply}
          color="bg-purple-500"
        />
      </div>

      {/* Per-session breakdown */}
      {sessionStats.length > 0 && (
        <Card className="mb-6">
          <CardHeader className="p-4">
            <h3 className="text-sm font-semibold text-ink-primary">Per-Session Breakdown</h3>
          </CardHeader>
          <CardBody className="p-0">
            <TableWrapper>
              <Table>
                <thead>
                  <tr>
                    <Th>Session</Th>
                    <Th>Status</Th>
                    <Th>Sent</Th>
                    <Th>Delivered</Th>
                    <Th>Failed</Th>
                    <Th>Replied</Th>
                  </tr>
                </thead>
                <tbody>
                  {sessionStats.map((ss, i) => {
                    // Trust the action over the probe: if this session has
                    // sent messages for THIS campaign, it's clearly working
                    // regardless of what the live connectivity probe says.
                    const sent = ss.sent ?? 0;
                    const probeSays = ss.sessionStatus;
                    const label = sent > 0
                      ? "Recently active"
                      : probeSays === "connected"
                      ? "Connected"
                      : probeSays === "completed"
                      ? "Connected"
                      : probeSays === "failed"
                      ? "Failed"
                      : "Probe says down";
                    const variant =
                      sent > 0 || probeSays === "connected" || probeSays === "completed"
                        ? "success"
                        : probeSays === "failed"
                        ? "danger"
                        : "neutral";
                    return (
                      <tr key={i}>
                        <Td className="text-xs font-medium text-ink-primary">Session {ss.sessionNumber}</Td>
                        <Td>
                          <Badge variant={variant}>{label}</Badge>
                        </Td>
                        <Td className="text-xs">{sent}</Td>
                        <Td className="text-xs">{ss.delivered ?? 0}</Td>
                        <Td className="text-xs">{ss.failed ?? 0}</Td>
                        <Td className="text-xs">{ss.replied ?? 0}</Td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </TableWrapper>
          </CardBody>
        </Card>
      )}

      {/* Executions table */}
      <Card>
        <CardHeader className="p-4">
          <h3 className="text-sm font-semibold text-ink-primary">Executions</h3>
        </CardHeader>
        <CardBody className="p-0">
          {executions.length === 0 ? (
            <div className="py-12">
              <EmptyState icon={FaEnvelope} title="No executions yet" description="Activate the campaign to start sending messages." />
            </div>
          ) : (
            <TableWrapper>
              <Table>
                <thead>
                  <tr>
                    <Th>Contact</Th>
                    <Th>Phone</Th>
                    <Th>Session</Th>
                    <Th>Status</Th>
                    <Th>Messages Sent</Th>
                    <Th>Replied</Th>
                    <Th>Last Sent</Th>
                    <Th></Th>
                  </tr>
                </thead>
                <tbody>
                  {executions.map((exec, i) => (
                    <tr key={i}>
                      <Td className="text-xs font-medium text-ink-primary">
                        {exec.contactName || exec.contact?.name || "Unknown"}
                      </Td>
                      <Td className="text-xs text-ink-muted">{exec.phone || exec.contact?.phone || ""}</Td>
                      <Td className="text-xs text-ink-muted">{exec.sessionNumber ?? ""}</Td>
                      <Td>
                        <Badge variant={
                          exec.status === "completed" ? "success" :
                          exec.status === "failed" ? "danger" :
                          exec.status === "active" ? "brand" : "neutral"
                        }>
                          {exec.status || "pending"}
                        </Badge>
                      </Td>
                      <Td className="text-xs">{exec.messagesSent ?? 0}</Td>
                      <Td>
                        {exec.replied
                          ? <Badge variant="success">Yes</Badge>
                          : <Badge variant="neutral">No</Badge>}
                      </Td>
                      <Td className="text-xs text-ink-muted">
                        {exec.lastSentAt ? new Date(exec.lastSentAt).toLocaleDateString() : "N/A"}
                      </Td>
                      <Td className="text-right">
                        <button
                          type="button"
                          onClick={() => handleUnassignLead(exec)}
                          title="Remove this lead from the campaign"
                          aria-label="Remove lead from campaign"
                          className="text-ink-faint hover:text-red-500 p-1.5 rounded-md hover:bg-red-50 transition-colors"
                        >
                          <FaTrash size={10} />
                        </button>
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </TableWrapper>
          )}
        </CardBody>
      </Card>
      </>
      )}
    </div>
  );
}

/* ════════════════════════════════════════════════
   Main Campaigns Component
   ════════════════════════════════════════════════ */
export default function Campaigns() {
  const modal = useModal();
  const navigate = useNavigate();
  const { campaignId: urlCampaignId } = useParams();
  const [campaigns, setCampaigns] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [templatesBySession, setTemplatesBySession] = useState({});
  const [loading, setLoading] = useState(true);

  // Views: "list" | "wizard" | "detail"
  const isWizardPath = typeof window !== "undefined" && window.location.pathname.endsWith("/campaigns/new");
  const initialView = urlCampaignId ? "detail" : isWizardPath ? "wizard" : "list";
  const [view, setView] = useState(initialView);
  const [detailId, setDetailId] = useState(urlCampaignId || null);

  // Keep URL in sync with view state so every campaign has a shareable URL
  useEffect(() => {
    if (view === "detail" && detailId) {
      if (window.location.pathname !== `/campaigns/${detailId}` &&
          !window.location.pathname.startsWith(`/campaigns/${detailId}/`)) {
        navigate(`/campaigns/${detailId}`, { replace: false });
      }
    } else if (view === "wizard") {
      if (window.location.pathname !== "/campaigns/new") {
        navigate("/campaigns/new", { replace: false });
      }
    } else if (view === "list") {
      if (window.location.pathname !== "/campaigns") {
        navigate("/campaigns", { replace: false });
      }
    }
  }, [view, detailId, navigate]);

  // Respond to back/forward and direct URL entry
  useEffect(() => {
    if (urlCampaignId && (view !== "detail" || detailId !== urlCampaignId)) {
      setView("detail");
      setDetailId(urlCampaignId);
    } else if (!urlCampaignId && view === "detail") {
      // URL no longer has a campaignId — drop back to list (unless on /new)
      if (!isWizardPath) {
        setView("list");
        setDetailId(null);
      }
    }
  }, [urlCampaignId, isWizardPath]); // eslint-disable-line react-hooks/exhaustive-deps

  // Wizard state
  const [wizardStep, setWizardStep] = useState(1);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    name: "",
    sessionIds: [],
    targetType: "manual",
    targetFilters: { tags: [], stages: [] },
    messages: [{ text: "", delayValue: 0, delayUnit: "minutes" }],
    duration: "month",
    pacingMode: "auto",
    pacingValue: null,
    pacingUnit: "minutes",
    leads: [],
    csvLeads: [],
    activateOnCreate: false,
    stopOnReply: true,
    // Daily send cap (per WhatsApp number) + batch rest. Off by default;
    // pre-filled with sensible ranges so enabling is one click.
    dailyLimitEnabled: false,
    dailyLimitMin: 80,
    dailyLimitMax: 100,
    weekendOverrideEnabled: false,
    weekendDailyLimitMin: 30,
    weekendDailyLimitMax: 50,
    batchRestEnabled: false,
    restEveryMin: 10,
    restEveryMax: 20,
    restDurationMinMinutes: 30,
    restDurationMaxMinutes: 60,
  });

  const fetchCampaigns = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiGet("/api/outreach");
      setCampaigns(res?.data || []);
    } catch {
      setCampaigns([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchSessions = useCallback(async () => {
    try {
      const res = await apiGet("/api/sessions");
      setSessions((res?.data || []).filter((s) => s.status === "connected"));
    } catch {
      setSessions([]);
    }
  }, []);

  const fetchTemplatesForSessions = useCallback(async (sessionIds = []) => {
    const cloudIds = (sessionIds || []).filter((sid) => {
      const session = sessions.find((s) => s.sessionId === sid);
      return session?.transportType === "walytic_cloud";
    });
    if (cloudIds.length === 0) {
      setTemplatesBySession({});
      return;
    }

    const entries = await Promise.all(cloudIds.map(async (sid) => {
      try {
        const res = await apiGet(`/api/cloud/sessions/${sid}/templates`);
        return [sid, res?.templates || []];
      } catch {
        return [sid, []];
      }
    }));
    setTemplatesBySession(Object.fromEntries(entries));
  }, [sessions]);

  useEffect(() => {
    fetchCampaigns();
    fetchSessions();
  }, [fetchCampaigns, fetchSessions]);

  useEffect(() => {
    fetchTemplatesForSessions(form.sessionIds || []);
  }, [form.sessionIds, fetchTemplatesForSessions]);

  const handleToggle = async (campaign, e) => {
    e?.stopPropagation();
    const res = await apiPut(`/api/outreach/${campaign.id}/toggle`);
    if (res?.success) fetchCampaigns();
    else modal.alert(res?.message || "Failed to toggle campaign", "error");
  };

  const handleDelete = async (campaign, e) => {
    e?.stopPropagation();
    if (!(await modal.confirm(`Delete "${campaign.name}"? This cannot be undone.`, { danger: true, confirmText: "Delete" }))) return;
    const res = await apiDelete(`/api/outreach/${campaign.id}`);
    if (res?.success) fetchCampaigns();
  };

  const handleQuickDuplicate = async (campaign, e) => {
    e?.stopPropagation();
    const newName = `Copy of ${campaign.name || "Campaign"}`;
    try {
      const res = await apiPost(`/api/outreach/${campaign.id}/duplicate`, {
        name: newName,
        withLeads: false,
      });
      if (res?.success) {
        modal.alert(
          `Duplicated as "${newName}". Open the new campaign to add leads or activate.`,
          "success"
        );
        fetchCampaigns();
      } else {
        modal.alert(res?.message || "Failed to duplicate campaign", "error");
      }
    } catch (err) {
      modal.alert(err?.message || "Failed to duplicate campaign", "error");
    }
  };

  const openWizard = () => {
    setForm({
      name: "",
      sessionIds: [],
      targetType: "manual",
      targetFilters: { tags: [], stages: [] },
      messages: [{ text: "", delayValue: 0, delayUnit: "minutes" }],
      duration: "month",
      pacingMode: "auto",
      pacingValue: null,
      pacingUnit: "minutes",
      leads: [],
      csvLeads: [],
      activateOnCreate: false,
      stopOnReply: true,
    });
    setWizardStep(1);
    setView("wizard");
  };

  const openDetail = (campaign) => {
    setDetailId(campaign.id);
    setView("detail");
  };

  const handleCreate = async () => {
    if (!form.name.trim() || (form.sessionIds || []).length === 0) return;
    setCreating(true);
    try {
      if (!isSendWindowComplete(form.sendWindow)) {
        modal.alert(
          "Send window is partially filled. Please complete all four fields (start time, end time, days, timezone) or turn it off.",
          "error"
        );
        setCreating(false);
        return;
      }

      // ── Lead validation ───────────────────────────────────────────
      // Block create when no leads will be enrolled. Each target mode has its
      // own failure shape, so the message is tailored to the mode.
      if (form.targetType === "manual" && (form.leads || []).length === 0) {
        modal.alert(
          "No leads selected. Pick at least one contact in Step 2, or switch to CSV upload / Filter mode.",
          "warning"
        );
        setCreating(false);
        return;
      }
      if (form.targetType === "csv" && (form.csvLeads || []).length === 0) {
        modal.alert(
          "No valid leads parsed from your CSV.\n\n" +
          "• Make sure one column is mapped as Phone (with country code, no spaces).\n" +
          "• Avoid Excel scientific notation (9.19E+11) — set the phone column format to Text before exporting.\n" +
          "• Need a template? Use the 'Download sample CSV' button above the upload field.",
          "error"
        );
        setCreating(false);
        return;
      }
      if (form.targetType === "filter") {
        const hasTags = (form.targetFilters?.tags || []).length > 0;
        const hasStages = (form.targetFilters?.stages || []).length > 0;
        if (!hasTags && !hasStages) {
          modal.alert(
            "Filter mode needs at least one tag or stage selected, otherwise the campaign matches zero contacts.",
            "warning"
          );
          setCreating(false);
          return;
        }
        // Resolve filters server-side to confirm there's actually a matching audience.
        try {
          const preview = await apiPost("/api/outreach/resolve-filters", {
            tags: form.targetFilters?.tags || [],
            stages: form.targetFilters?.stages || [],
          });
          const matchCount = preview?.count ?? preview?.total ?? (Array.isArray(preview?.contacts) ? preview.contacts.length : 0);
          if (!matchCount) {
            modal.alert(
              "Your filters match 0 contacts.\n\nWiden the tags/stages or add leads to your CRM that match these criteria, then try again.",
              "warning"
            );
            setCreating(false);
            return;
          }
        } catch {
          // If preview fails, fall through and let the backend respond — don't block on a network blip.
        }
      }
      const sw = form.sendWindow || {};
      const isCsv = form.targetType === "csv";
      const payload = {
        name: form.name.trim(),
        sessionIds: form.sessionIds,
        // CSV is enrolled separately via /assign-csv after create — don't pass leads here.
        targetType: isCsv ? "manual" : form.targetType,
        // Tell the server to skip its empty-leads guard; CSV rows are POSTed next.
        deferEnrollment: isCsv ? true : undefined,
        targetFilters: form.targetType === "filter" ? form.targetFilters : undefined,
        messages: form.messages,
        duration: form.duration,
        // Pacing: send null when "Auto" (random 10–90s default on the server),
        // otherwise the user's custom interval. The server treats null pacingValue
        // as auto-mode and applies cumulative random jitter per lead.
        pacingValue:
          form.pacingMode === "custom" && Number(form.pacingValue) > 0
            ? Number(form.pacingValue)
            : null,
        pacingUnit:
          form.pacingMode === "custom" && Number(form.pacingValue) > 0
            ? form.pacingUnit || "minutes"
            : null,
        leads: form.targetType === "manual"
          ? (form.leads || []).map((l) => l.id || l)
          : undefined,
        sendWindowStart: sw.sendWindowStart || null,
        sendWindowEnd: sw.sendWindowEnd || null,
        sendWindowDays: Array.isArray(sw.sendWindowDays) ? sw.sendWindowDays : [],
        sendWindowTimezone: sw.sendWindowTimezone || null,
        stopOnReply: form.stopOnReply !== false,
        // Daily cap (per number) + weekend override — null when disabled.
        dailyLimitMin: form.dailyLimitEnabled ? Number(form.dailyLimitMin) || null : null,
        dailyLimitMax: form.dailyLimitEnabled ? Number(form.dailyLimitMax) || null : null,
        weekendDailyLimitMin:
          form.dailyLimitEnabled && form.weekendOverrideEnabled ? Number(form.weekendDailyLimitMin) || null : null,
        weekendDailyLimitMax:
          form.dailyLimitEnabled && form.weekendOverrideEnabled ? Number(form.weekendDailyLimitMax) || null : null,
        // Batch rest — null when disabled.
        restEveryMin: form.batchRestEnabled ? Number(form.restEveryMin) || null : null,
        restEveryMax: form.batchRestEnabled ? Number(form.restEveryMax) || null : null,
        restDurationMinMinutes: form.batchRestEnabled ? Number(form.restDurationMinMinutes) || null : null,
        restDurationMaxMinutes: form.batchRestEnabled ? Number(form.restDurationMaxMinutes) || null : null,
      };
      const res = await apiPost("/api/outreach", payload);
      if (!res?.success) {
        modal.alert(res?.message || "Failed to create campaign", "error");
        return;
      }

      let enrolled = res.enrolled ?? 0;
      let requested = res.requested ?? 0;
      let csvFailures = [];

      // CSV mode: enroll via /assign-csv (unified backend path with upsert + soft-fallback).
      if (isCsv && res.data?.id && (form.csvLeads || []).length > 0) {
        // Excel often exports long phone numbers as scientific notation (9.19E+11)
        // and the original digits are already lost. Warn before sending.
        const sciRows = form.csvLeads.filter((r) => /[eE][+-]?\d/.test(String(r.phone || "")));
        if (sciRows.length > 0) {
          modal.alert(
            `${sciRows.length} of ${form.csvLeads.length} phone numbers in your CSV were saved in scientific notation by Excel ` +
            `(e.g. ${sciRows[0].phone}). The original digits are lost — those rows can't be enrolled.\n\n` +
            `Fix: open the source file, select the phone column, set its format to Text, then re-export as CSV. ` +
            `The campaign was created — you can re-upload a clean CSV from the Analytics tab.`,
            "warning"
          );
        }
        const rows = form.csvLeads.map((r) => ({
          phone: r.phone,
          firstName: r.firstName || undefined,
          lastName: r.lastName || undefined,
          email: r.email || undefined,
          companyName: r.companyName || undefined,
          tags: r.tags || undefined,
          customFields: Array.isArray(r.customFields) && r.customFields.length > 0
            ? r.customFields
            : undefined,
        }));
        const csvRes = await apiPost(`/api/outreach/${res.data.id}/assign-csv`, { rows });
        if (csvRes?.success) {
          enrolled += csvRes.enrolled || 0;
          requested += csvRes.total || rows.length;
          csvFailures = (csvRes.results || []).filter((r) => r.status !== "enrolled");
        }
      }

      // Activate if toggled on
      if (form.activateOnCreate && res.data?.id) {
        await apiPut(`/api/outreach/${res.data.id}/toggle`);
      }

      const skipped = Math.max(0, requested - enrolled);
      let summary = `Campaign created.`;
      if (requested > 0) summary += ` Enrolled ${enrolled} of ${requested} lead${requested === 1 ? "" : "s"}.`;
      if (csvFailures.length > 0) {
        summary += `\n\nCSV rows that didn’t enroll:\n` +
          csvFailures.slice(0, 8).map((r) =>
            `${r.phone || r.contactId || "(no phone)"}: ${r.status}${r.message ? ` (${r.message})` : ""}`
          ).join("\n");
      } else if (skipped > 0) {
        const reasons = (res.results || [])
          .filter((r) => r.status !== "enrolled")
          .map((r) => `${r.contactId}: ${r.status}${r.message ? ` (${r.message})` : ""}`)
          .join("\n");
        summary += `\n\n${skipped} lead${skipped === 1 ? "" : "s"} couldn't be enrolled:\n${reasons}`;
      }
      modal.alert(summary, skipped > 0 || csvFailures.length > 0 ? "warning" : "success");

      if (res.data?.id) {
        setDetailId(res.data.id);
        setView("detail");
        fetchCampaigns();
      } else {
        setView("list");
        fetchCampaigns();
      }
    } catch (err) {
      modal.alert(err?.message || "Failed to create campaign", "error");
    } finally {
      setCreating(false);
    }
  };

  // ── DETAIL VIEW ──
  if (view === "detail" && detailId) {
    return (
      <CampaignDetail
        campaignId={detailId}
        onBack={() => { setView("list"); setDetailId(null); fetchCampaigns(); }}
      />
    );
  }

  // ── WIZARD VIEW ──
  if (view === "wizard") {
    const canNext =
      wizardStep === 1 ? form.name.trim() && (form.sessionIds || []).length > 0 :
      wizardStep === 2 ? true :
      wizardStep === 3 ? (form.messages || []).some(hasMessageContent) :
      true;

    return (
      <div className="page-content">
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <button className="btn-ghost btn-sm flex items-center gap-1.5" onClick={() => setView("list")}>
            <FaArrowLeft size={11} /> Back
          </button>
          <div className="w-px h-5 bg-surface-border hidden sm:block" />
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-brand flex items-center justify-center">
              <FaBullhorn size={12} className="text-white" />
            </div>
            <h2 className="text-sm font-semibold text-ink-primary">New Campaign</h2>
          </div>
        </div>

        <StepIndicator current={wizardStep} steps={["Setup", "Target Leads", "Messages", "Review"]} />

        <div className="w-full">
          {wizardStep === 1 && <WizardStep1 form={form} setForm={setForm} sessions={sessions} />}
          {wizardStep === 2 && <WizardStep2 form={form} setForm={setForm} />}
          {wizardStep === 3 && (
            <WizardStep3
              form={form}
              setForm={setForm}
              sessions={sessions}
              templatesBySession={templatesBySession}
            />
          )}
          {wizardStep === 4 && (
            <WizardStep4 form={form} setForm={setForm} creating={creating} onCreate={handleCreate} />
          )}
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between gap-2 mt-6">
          <button
            type="button"
            className="btn-secondary btn-sm"
            disabled={wizardStep === 1}
            onClick={() => setWizardStep((s) => s - 1)}
          >
            Previous
          </button>
          {wizardStep < 4 ? (
            <button
              type="button"
              className="btn-primary btn-sm flex items-center gap-1.5"
              disabled={!canNext}
              onClick={() => setWizardStep((s) => s + 1)}
            >
              Next <FaChevronRight size={9} />
            </button>
          ) : null}
        </div>
      </div>
    );
  }

  // ── LIST VIEW ──
  return (
    <div className="page-content">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <PageHeader title="Campaigns" subtitle="Outreach campaigns to engage leads over time" guideUrl="/guide/campaigns" />
        <button className="btn-primary flex items-center justify-center gap-1.5 w-full sm:w-auto" onClick={openWizard}>
          <FaPlus size={11} /> New Campaign
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Spinner size="lg" />
        </div>
      ) : campaigns.length === 0 ? (
        <Card>
          <CardBody className="py-16">
            <EmptyState
              icon={FaBullhorn}
              title="No campaigns yet"
              description="Create your first outreach campaign to send timed WhatsApp messages to your leads."
            />
          </CardBody>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {campaigns.map((c) => {
            const stats = c.aggregateStats || {};
            return (
              <Card
                key={c.id}
                className="hover:shadow-md hover:border-brand/20 transition-all cursor-pointer group"
                onClick={() => openDetail(c)}
              >
                <CardBody className="p-0">
                  <div className="px-4 pt-4 pb-3">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2.5 min-w-0">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${c.active ? "bg-green-500" : "bg-ink-faint"}`}>
                          <FaBullhorn size={12} className="text-white" />
                        </div>
                        <div className="min-w-0">
                          <h3 className="text-sm font-semibold text-ink-primary truncate group-hover:text-brand transition-colors">
                            {c.name}
                          </h3>
                        </div>
                      </div>
                      <Badge variant={c.active ? "success" : "neutral"}>
                        {c.active ? "Active" : "Inactive"}
                      </Badge>
                    </div>

                    {/* Meta row */}
                    <div className="flex items-center gap-4 mt-3">
                      <div className="flex items-center gap-1.5">
                        <FaEnvelope size={9} className="text-ink-faint" />
                        <span className="text-xs text-ink-muted">
                          {(c.messages || []).length} message{(c.messages || []).length !== 1 ? "s" : ""}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <FaWhatsapp size={9} className="text-ink-faint" />
                        <span className="text-xs text-ink-muted">
                          {(c.sessionIds || []).length} session{(c.sessionIds || []).length !== 1 ? "s" : ""}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <FaUsers size={9} className="text-ink-faint" />
                        <span className="text-xs text-ink-muted">{c.leadCount ?? 0} leads</span>
                      </div>
                    </div>

                    {/* Stats row */}
                    <div className="flex items-center gap-4 mt-2 pt-2 border-t border-surface-border">
                      <div className="flex items-center gap-1.5">
                        <FaPaperPlane size={8} className="text-blue-400" />
                        <span className="text-2xs text-ink-muted">Sent <span className="font-medium text-ink-primary">{stats.sent ?? 0}</span></span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <FaCheck size={8} className="text-green-400" />
                        <span className="text-2xs text-ink-muted">Delivered <span className="font-medium text-ink-primary">{stats.delivered ?? 0}</span></span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <FaReply size={8} className="text-purple-400" />
                        <span className="text-2xs text-ink-muted">Replied <span className="font-medium text-ink-primary">{stats.replied ?? 0}</span></span>
                      </div>
                    </div>
                  </div>

                  {/* Action footer */}
                  <div
                    className="flex items-center gap-2 px-4 py-2.5 border-t border-surface-border bg-surface-bg/50"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <button className="btn-ghost btn-sm flex items-center gap-1" onClick={() => openDetail(c)}>
                      <FaEdit size={10} /> Edit
                    </button>
                    <button
                      className={`btn-sm flex items-center gap-1 ${c.active ? "btn-secondary" : "btn-primary"}`}
                      onClick={(e) => handleToggle(c, e)}
                    >
                      {c.active ? <><FaToggleOff size={11} /> Pause</> : <><FaToggleOn size={11} /> Activate</>}
                    </button>
                    <button
                      className="btn-secondary btn-sm btn-icon ml-auto"
                      onClick={(e) => handleQuickDuplicate(c, e)}
                      title="Duplicate this campaign (no leads)"
                    >
                      <FaCopy size={10} />
                    </button>
                    <button className="btn-danger btn-sm btn-icon" onClick={(e) => handleDelete(c, e)}>
                      <FaTrash size={10} />
                    </button>
                  </div>
                </CardBody>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
